import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabase';
import WorkoutLogger from './WorkoutLogger';
import { toastError, toastSuccess } from './ui/toast';
import { SET_CATEGORIES, formatCategoryDisplay, getPositionLabel, getClientDescription } from './constants/blockTypes';
// CategoryBadge import removed (unused)
import TechniqueInfoTooltip from './components/ui/TechniqueInfoTooltip';
import WorkoutSummaryModal from './components/workout-summary/WorkoutSummaryModal';
import MonthlyCheckIn from './components/MonthlyCheckIn';
import { checkEligibilityForCheckIn, checkInBannerText } from './services/checkInService';
import { generateNextWeek } from './services/weekGeneratorService';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import cipherLogo from './assets/cipher-icon-only-transparent.png';
import { Lock, ArrowRight } from 'lucide-react';
import CipherButton from './components/ui/CipherButton';
import ScanReminder from './components/bodystats/ScanReminder';
import { typography as metricType } from './theme';
import { queueSessionComplete, getPendingSessionCompletions, removePendingSessionCompletion } from './utils/offlineQueue';
import OnboardingModal, { shouldShowOnboarding } from './components/OnboardingModal';
import PWAInstallGuide from './components/PWAInstallGuide';
import { tourMayShow, onTourReleased } from './utils/homeScreenGuide';
import { getCoachNotes, markCoachNoteRead } from './services/coachNotesService';
import { planMissedDays, loadTrainedOpenSessionIds, openSessionsToClose } from './services/sessionAccountingService';
import * as Sentry from '@sentry/react';
import { createCoalescedRefresher } from './utils/coalescedRefresher';
import { colors as theme } from './theme';
import { colors as shared } from './theme';
import { dayTitle } from './utils/dayTitle';
import { markWorkoutCompleted, claimPromptSlot } from './utils/promptOncePerVisit';
import { SessionStart, SessionFinish } from './components/session/SessionMoments';
import { isOfflineSessionId } from './services/offlineSessionService';
import { RESUMABLE_SESSION_WINDOW_MS } from './services/sessionAccountingService';
import { oneRowPerWeek, calendarWeek, weekEndingSoon, whenWeekEnds } from './utils/programWeeks';

// How often the open program screen re-reads completion data by itself. It already re-reads after
// every saved set, on coming back to the app and after finishing a day, so the timer only has to
// catch changes made somewhere else (a coach editing the program, another device). It was 10
// seconds: three queries, six times a minute, from every open phone, all day, on a database that
// froze on 2026-10-03 when it ran out of headroom.
const COMPLETION_POLL_MS = 60000;
// Background re-reads wait this long first, so a burst (weight saved, then reps a second later)
// is one read, not two.
const COMPLETION_REFRESH_DELAY_MS = 1500;

// Typography scale
const typography = {
  h1: { fontSize: '32px', fontWeight: '700', lineHeight: '1.2' },
  h2: { fontSize: '20px', fontWeight: '700', lineHeight: '1.3' },
  h3: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
  body: { fontSize: '15px', fontWeight: '400', lineHeight: '1.6' },
  label: { fontSize: '12px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase' },
  small: { fontSize: '13px', fontWeight: '500', lineHeight: '1.5' },
  tiny: { fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' },
};

// Cipher color palette
const colors = {
  // Surfaces
  bg: theme.bg,
  surface1: theme.surface1,
  surface2: theme.surface2,
  surface3: theme.surface3,

  // Borders
  border: 'rgba(42, 53, 98, 0.8)',
  borderLight: 'rgba(58, 69, 120, 0.8)',
  borderAccent: 'rgba(212, 175, 55, 0.3)',

  // Text
  text: theme.text,
  textSecondary: '#C0C8DC',
  textMuted: theme.textMuted,

  // Accent (gold)
  accent: theme.gold,
  accentGlow: 'rgba(212, 175, 55, 0.15)',
  accentSubtle: 'rgba(212, 175, 55, 0.08)',

  // Status
  success: theme.success,
  successGlow: 'rgba(52, 199, 89, 0.15)',
  error: theme.danger,
};


const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// One workout on this device: the person, their program and the day (Codex's review of PR 116, S2)
const workoutKey = (userId, programId, dayId) => `${userId}:${programId}:${dayId}`;
const readStore = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
};
const writeStore = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* not kept */ }
};
// Entries younger than maxAge, and none dated in the future
const freshEntries = (store, maxAge) => {
  const now = Date.now();
  return Object.fromEntries(Object.entries(store).filter(([, v]) => {
    const at = typeof v === 'number' ? v : v?.at;
    return typeof at === 'number' && at <= now + 60000 && now - at < maxAge;
  }));
};

// A workout started without an energy rating is not asked again on this device for 12 hours
const START_SKIP_KEY = 'cipher_session_start_skipped';
const START_SKIP_MS = 12 * 60 * 60 * 1000;
const startedWithoutRating = (key) => key in freshEntries(readStore(START_SKIP_KEY), START_SKIP_MS);
const rememberStartedWithoutRating = (key) => {
  writeStore(START_SKIP_KEY, { ...freshEntries(readStore(START_SKIP_KEY), START_SKIP_MS), [key]: Date.now() });
};

// The energy a client chose, kept on this device until it is saved on the session: it survives a session
// that is not ready yet, a refused save, a refresh and being offline (Codex's review of PR 116, M1). It lasts
// as long as a session can be resumed.
const PENDING_ENERGY_KEY = 'cipher_pending_energy';
// Also held in memory: a full or blocked store on the device cannot lose the answer while the app is open
// (Codex's re-check of PR 116, R1)
const pendingInMemory = new Map();
const pendingEnergy = (key) => {
  const stored = freshEntries(readStore(PENDING_ENERGY_KEY), RESUMABLE_SESSION_WINDOW_MS)[key] || null;
  const held = pendingInMemory.has(key)
    ? freshEntries({ [key]: pendingInMemory.get(key) }, RESUMABLE_SESSION_WINDOW_MS)[key] || null
    : null;
  if (!stored) return held;
  if (!held) return stored;
  return held.at >= stored.at ? held : stored;
};
const keepPendingEnergy = (key, rating) => {
  const entry = { rating, at: Date.now() };
  pendingInMemory.set(key, entry);
  writeStore(PENDING_ENERGY_KEY, { ...freshEntries(readStore(PENDING_ENERGY_KEY), RESUMABLE_SESSION_WINDOW_MS), [key]: entry });
};
// Removed only if it is still the answer that was saved (not one chosen since)
const dropPendingEnergy = (key, at) => {
  if (pendingInMemory.get(key)?.at === at) pendingInMemory.delete(key);
  const store = freshEntries(readStore(PENDING_ENERGY_KEY), RESUMABLE_SESSION_WINDOW_MS);
  if (store[key] && store[key].at === at) { delete store[key]; writeStore(PENDING_ENERGY_KEY, store); }
};

const estimateDuration = (exercises) => {
  if (!exercises || exercises.length === 0) return 0;
  const totalSets = exercises.reduce((sum, ex) => sum + (ex.sets || 3), 0);
  return Math.round(totalSets * 2);
};

const getPrimaryMuscle = (exercises) => {
  if (!exercises || exercises.length === 0) return null;
  const muscles = {};
  exercises.forEach(ex => {
    const muscle = ex.exercise?.muscle_group || ex.muscle_group;
    if (muscle) muscles[muscle] = (muscles[muscle] || 0) + 1;
  });
  const sorted = Object.entries(muscles).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] || null;
};

// Skeleton component for loading states
const Skeleton = ({ width, height, style }) => (
  <div
    className="skeleton"
    style={{
      width: width || '100%',
      height: height || '20px',
      backgroundColor: colors.surface2,
      borderRadius: '8px',
      ...style,
    }}
  />
);

const ExerciseSkeleton = () => (
  <div style={{
    padding: '20px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
  }}>
    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
      <Skeleton width="80px" height="80px" style={{ borderRadius: '14px', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <Skeleton width="60%" height="18px" style={{ marginBottom: '12px' }} />
        <Skeleton width="40%" height="14px" style={{ marginBottom: '16px' }} />
        <div style={{ display: 'flex', gap: '8px' }}>
          <Skeleton width="60px" height="36px" />
          <Skeleton width="60px" height="36px" />
          <Skeleton width="60px" height="36px" />
        </div>
      </div>
    </div>
  </div>
);

const ClientProgramView = ({ userName, topNotice = null, onShowProgress = null, guideRole = null }) => {
  const [program, setProgram] = useState(null);
  const [assignedAt, setAssignedAt] = useState(null); // Date of current assignment - used to filter out old sessions
  const [weekClock, setWeekClock] = useState(null); // Start of week 1 for the calendar (week_clock_from, else assigned_at)
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const [completionData, setCompletionData] = useState({});
  const [missedDayIds, setMissedDayIds] = useState(new Set());
  const [exerciseFilter, setExerciseFilter] = useState('all');
  const [contentKey, setContentKey] = useState(0); // For animation
  const [exerciseStatuses, setExerciseStatuses] = useState({}); // { peId: { status: 'done'|'skipped', skippedReason?: string } }
  const [localSetCompletions, setLocalSetCompletions] = useState(new Set()); // Track peIds with completed/skipped sets locally
  const [dayCompleting, setDayCompleting] = useState(false);
  const [programComplete] = useState(false);
  const [generatingWeek, setGeneratingWeek] = useState(false);
  const [weekGenerationFailed, setWeekGenerationFailed] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [completedSessionId, setCompletedSessionId] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [energySubmitted, setEnergySubmitted] = useState(false); // true once rated or skipped
  // The energy saved on the session this day resumes, once checked, and which session that is: the start
  // screen shows only without one (Codex's review of PR 116, M2)
  const [savedEnergy, setSavedEnergy] = useState({ dayId: null, sessionId: null, rating: null });
  // The session the exercises resolved for the day on screen; a refresh of what is pending on the device
  const loggerSession = useRef({ dayId: null, sessionId: null });
  const [, setPendingTick] = useState(0);
  const lockRequest = useRef(0);
  const currentDayIdRef = useRef(null);
  const savedEnergyRef = useRef(savedEnergy);
  savedEnergyRef.current = savedEnergy;
  const completingRef = useRef(false);
  const energyToasted = useRef(new Set());
  const [finishOpen, setFinishOpen] = useState(false);
  const [sessionLocked, setSessionLocked] = useState(false);
  const [checkInEligibility, setCheckInEligibility] = useState(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(shouldShowOnboarding);
  // The add-to-home-screen guide comes first; the welcome tour waits until it is closed
  const [tourMayStart, setTourMayStart] = useState(tourMayShow);
  useEffect(() => onTourReleased(() => setTourMayStart(true)), []);
  const [coachSessionNote, setCoachSessionNote] = useState(null); // { sessionId, note } from last session
  const [coachNoteVisible, setCoachNoteVisible] = useState(true);
  const [unreadCoachNotes, setUnreadCoachNotes] = useState([]); // standalone coach_notes with read_at IS NULL, oldest-unread-first shown one at a time
  const [preloadedOverrides, setPreloadedOverrides] = useState(new Map()); // peId → override row
  const [preloadedFlags, setPreloadedFlags] = useState(new Map());         // peId → flag row
  const [preloadedResolvedNotes, setPreloadedResolvedNotes] = useState(new Map()); // peId → coach resolution note
  const lastFullFetchRef = useRef(0); // timestamp of last fetchUserAndProgram completion

  // Background re-reads of completion data (the timer, coming back to the app, each saved set) go
  // through one gate: one at a time, a burst becomes one, and they never queue up behind a slow
  // database. Reads that a caller waits for still call fetchCompletionData directly.
  const fetchCompletionDataRef = useRef(null);
  const completionRefresherRef = useRef(null);
  useEffect(() => {
    const refresher = createCoalescedRefresher(
      (...args) => fetchCompletionDataRef.current?.(...args),
      { delayMs: COMPLETION_REFRESH_DELAY_MS }
    );
    completionRefresherRef.current = refresher;
    return () => {
      refresher.cancel();
      completionRefresherRef.current = null;
    };
  }, []);
  const refreshCompletionSoon = useCallback((...args) => {
    completionRefresherRef.current?.request(...args);
  }, []);

  // Optimized data fetching - batch queries instead of N+1
  // silent=true skips the loading spinner (used for background refreshes during active workout)
  const fetchUserAndProgram = useCallback(async (silent = false, skipNavigation = false) => {
    try {
      setError(null);
      if (!silent) setLoading(true);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      setUserId(user.id);

      // Fetch most recent completed session that has a coach note (not yet dismissed)
      const { data: noteSession } = await supabase
        .from('workout_sessions')
        .select('id, coach_notes')
        .eq('user_id', user.id)
        .not('coach_notes', 'is', null)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (noteSession?.coach_notes) {
        const dismissedKey = `cipher_coach_note_seen_${noteSession.id}`;
        if (!localStorage.getItem(dismissedKey)) {
          setCoachSessionNote({ sessionId: noteSession.id, note: noteSession.coach_notes });
          setCoachNoteVisible(true);
        }
      }

      // Fetch any unread standalone coach notes — surfaced here (not just the Profile
      // tab) so a message from the coach can't get missed the way relying on a push
      // notification's click behavior can.
      getCoachNotes(user.id)
        .then(notes => setUnreadCoachNotes(notes.filter(n => !n.read_at)))
        .catch(() => {});

      // Batch-load overrides and flags once per page load (prevents N+1 in WorkoutLogger)
      const [overridesRes, flagsRes] = await Promise.all([
        supabase
          .from('client_exercise_overrides')
          .select('program_exercise_id, substitute_exercise_id, notes, substitute:substitute_exercise_id(id, name, gif_url, exercise_type, duration_unit, movement_category, is_bodyweight, is_assisted, bodyweight_multiplier, is_unilateral)')
          .eq('user_id', user.id),
        supabase
          .from('exercise_flags')
          .select('*')
          .eq('user_id', user.id)
          .is('resolved_at', null),
      ]);
      setPreloadedOverrides(new Map((overridesRes.data || []).map(o => [o.program_exercise_id, o])));
      setPreloadedFlags(new Map((flagsRes.data || []).map(f => [f.program_exercise_id, f])));

      // Load recently-resolved flags that have a coach note — show feedback to client for 14 days
      const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data: resolvedWithNotes } = await supabase
        .from('exercise_flags')
        .select('program_exercise_id, resolution_notes')
        .eq('user_id', user.id)
        .not('resolved_at', 'is', null)
        .not('resolution_notes', 'is', null)
        .gte('resolved_at', twoWeeksAgo);
      setPreloadedResolvedNotes(new Map((resolvedWithNotes || []).map(f => [f.program_exercise_id, f.resolution_notes])));

      // Get program assignment
      let programId = null;
      // week_clock_from comes with migration 092: only while the database does not have it yet (42703, that
      // column named) is the assignment read without it. Any other failure is tried once more as it was and
      // then stops the load, before anything moves: a read without the clock would count weeks from the
      // assignment and undo the coach's Set Week (Codex's review 045, R1).
      const readAssignment = (columns) => supabase
        .from('program_assignments')
        .select(columns)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();
      const ASSIGNMENT_COLUMNS = 'program_id, assigned_at, current_week_index, current_day_index, current_week';
      const WITH_CLOCK = `${ASSIGNMENT_COLUMNS}, week_clock_from`;
      const noClockColumn = (err) => err?.code === '42703' && /week_clock_from/.test(err.message || '');
      let { data: assignment, error: assignmentErr } = await readAssignment(WITH_CLOCK);
      if (noClockColumn(assignmentErr)) ({ data: assignment, error: assignmentErr } = await readAssignment(ASSIGNMENT_COLUMNS));
      else if (assignmentErr) ({ data: assignment, error: assignmentErr } = await readAssignment(WITH_CLOCK));
      if (assignmentErr) throw new Error('Could not load your programme. Please try again.');

      let localAssignedAt = null;
      let localWeekClock = null;
      let localCurrentWeek = null;
      if (assignment) {
        programId = assignment.program_id;
        localAssignedAt = assignment.assigned_at || null;
        // A coach's Set Week moves the clock (migration 092); otherwise weeks count from the assignment
        localWeekClock = assignment.week_clock_from || localAssignedAt;
        setWeekClock(localWeekClock);
        // Store assignment date so WorkoutLogger only loads sessions from this assignment period
        setAssignedAt(localAssignedAt);
        // Restore saved position if available (used by determineAndSetCurrentWeek below)
        localCurrentWeek = assignment.current_week ?? null;
      } else {
        // Fallback
        const { data: fallbackProgram } = await supabase
          .from('programs')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        programId = fallbackProgram?.id;
        setAssignedAt(null);
      }

      if (!programId) {
        setLoading(false);
        return;
      }

      // Fetch program details
      const { data: programData, error: programError } = await supabase
        .from('programs')
        .select('*')
        .eq('id', programId)
        .single();

      if (programError) throw programError;

      setProgram({ ...programData });

      // OPTIMIZED: Fetch all weeks in one query
      const { data: weekRows, error: weeksError } = await supabase
        .from('program_weeks')
        .select('*')
        .eq('program_id', programId)
        .order('week_number');

      if (weeksError) throw weeksError;
      // One row per week number: a week numbered twice made the screen count one week too many and move
      // the client past weeks that did not exist (utils/programWeeks.js)
      const weeksData = oneRowPerWeek(weekRows);
      if (!weeksData?.length) {
        setWeeks([]);
        setLoading(false);
        return;
      }

      const weekIds = weeksData.map(w => w.id);

      // OPTIMIZED: Fetch all days for all weeks in one query
      const { data: allDays } = await supabase
        .from('program_days')
        .select('*')
        .in('week_id', weekIds)
        .order('day_number');

      const dayIds = allDays?.map(d => d.id) || [];

      // Check if program uses blocks (new structure)
      const usesBlocks = programData.uses_blocks;

      let weeksWithData;
      let allProgramExercises = [];

      if (usesBlocks) {
        // Fetch blocks for new programs
        const { data: allBlocks } = await supabase
          .from('exercise_blocks')
          .select('*')
          .in('day_id', dayIds)
          .order('block_order');

        const blockIds = allBlocks?.map(b => b.id) || [];

        // Fetch block items
        const { data: allBlockItems } = await supabase
          .from('block_items')
          .select('*')
          .in('block_id', blockIds)
          .order('position');

        const exerciseIds = [...new Set(allBlockItems?.map(bi => bi.exercise_id).filter(Boolean) || [])];

        const { data: allExercises } = await supabase
          .from('exercises')
          .select('id, name, video_url, gif_url, muscle_group, movement_category, exercise_type, duration_unit, is_bodyweight, is_assisted, bodyweight_multiplier, is_unilateral')
          .in('id', exerciseIds);

        const exerciseMap = new Map(allExercises?.map(e => [e.id, e]) || []);

        // Group blocks by day
        const blocksByDay = {};
        (allBlocks || []).forEach(block => {
          if (!blocksByDay[block.day_id]) blocksByDay[block.day_id] = [];
          block.items = (allBlockItems || []).filter(bi => bi.block_id === block.id);
          blocksByDay[block.day_id].push(block);
        });

        // Build weeks with blocks converted to exercises (for backward compatibility)
        weeksWithData = weeksData.map(week => ({
          ...week,
          days: (allDays || [])
            .filter(d => d.week_id === week.id)
            .map(day => {
              const dayBlocks = blocksByDay[day.id] || [];
              // Convert blocks to flat exercise list with block metadata
              const exercises = [];
              dayBlocks.forEach((block, blockIndex) => {
                block.items.forEach(item => {
                  const pe = {
                    id: item.id,
                    day_id: day.id,
                    exercise_id: item.exercise_id,
                    sets: item.sets,
                    reps: item.reps,
                    load_percentage: item.load_percentage,
                    prescribed_weight: item.prescribed_weight,
                    notes: item.notes,
                    exercise_order: (block.block_order * 10) + item.position,
                    exercise: exerciseMap.get(item.exercise_id) || null,
                    // Block metadata for display
                    block_index: blockIndex,
                    block_type: block.block_type,
                    set_category: block.set_category,
                    category_params: block.category_params,
                    position_label: getPositionLabel(blockIndex, item.position),
                    rest_seconds: block.rest_seconds,
                    is_last_in_block: item.position === block.items.length,
                  };
                  exercises.push(pe);
                  allProgramExercises.push(pe);
                });
              });
              return { ...day, exercises, blocks: dayBlocks };
            }),
        }));
      } else {
        // Legacy: Fetch program_exercises
        const { data: legacyExercises } = await supabase
          .from('program_exercises')
          .select('*')
          .in('day_id', dayIds)
          .order('exercise_order');

        allProgramExercises = legacyExercises || [];

        const exerciseIds = [...new Set(allProgramExercises.map(pe => pe.exercise_id).filter(Boolean))];

        const { data: allExercises } = await supabase
          .from('exercises')
          .select('id, name, video_url, gif_url, muscle_group, movement_category, exercise_type, duration_unit, is_bodyweight, is_assisted, bodyweight_multiplier, is_unilateral')
          .in('id', exerciseIds);

        const exerciseMap = new Map(allExercises?.map(e => [e.id, e]) || []);

        weeksWithData = weeksData.map(week => ({
          ...week,
          days: (allDays || [])
            .filter(d => d.week_id === week.id)
            .map(day => ({
              ...day,
              exercises: allProgramExercises
                .filter(pe => pe.day_id === day.id)
                .map(pe => ({
                  ...pe,
                  exercise: exerciseMap.get(pe.exercise_id) || null,
                })),
            })),
        }));
      }

      // Pre-set week index from the saved current_week before rendering.
      // Without this, the default selectedWeekIndex=0 causes a brief render showing
      // week 1 exercises even when the client is on week 2+, creating a race window
      // where both weeks' exercises are mounted and can be accidentally flagged.
      // determineAndSetCurrentWeek below will still refine this to the first incomplete day.
      const safeInitialWeekIndex = Math.max(0, Math.min(
        (localCurrentWeek ?? 1) - 1,
        weeksWithData.length - 1
      ));
      setSelectedWeekIndex(safeInitialWeekIndex);
      setWeeks(weeksWithData);

      // Auto-advance if real-world time has passed weeks the client didn't finish
      // Updates DB and returns the corrected currentWeek so determineAndSetCurrentWeek lands correctly
      const effectiveCurrentWeek = await autoAdvanceMissedWeeks(
        user.id, programId, weeksWithData, localAssignedAt, localCurrentWeek ?? 1, localWeekClock
      );
      if (effectiveCurrentWeek !== localCurrentWeek) {
        localCurrentWeek = effectiveCurrentWeek;
      }

      // Fetch completion data — only count logs from the current assignment period
      await fetchCompletionData(user.id, dayIds, allProgramExercises || [], localAssignedAt);

      // Always auto-detect current week from DB — prevents clients from landing on wrong week
      // Skip re-navigation if user is mid-session (skipNavigation=true) to avoid jumping away while training
      if (!skipNavigation) {
        await determineAndSetCurrentWeek(weeksWithData, user.id, programId, dayIds, allProgramExercises || [], localAssignedAt, localCurrentWeek);
      }

      lastFullFetchRef.current = Date.now();
      setLoading(false);
    } catch (err) {
      // A background refresh that fails (no signal, a dropped request) must not take the workout
      // off the screen and put an error page in its place: the program already loaded is still
      // good, and the client may be in the middle of logging it. The next return to the app
      // tries again, because lastFullFetchRef was not moved on.
      // Only for a true background refresh of a program that has loaded before. A first load, or
      // "Try Again", still shows the error: with nothing loaded there is nothing to keep.
      if (silent === true && lastFullFetchRef.current > 0) {
        console.error('Background refresh of the program failed:', err);
        return;
      }
      setError(err.message);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-advance weeks when real calendar time has passed without the client finishing
  // Returns the new currentWeek number (may be unchanged if not behind). Weeks are counted from the week
  // clock (the coach's Set Week, migration 092) when there is one; sessions still from assignedAt.
  const autoAdvanceMissedWeeks = async (userId, programId, weeksData, assignedAt, currentWeek, weekClock = null) => {
    if (!assignedAt) return currentWeek;

    const now = new Date();
    const assigned = new Date(weekClock || assignedAt);
    const expectedWeek = calendarWeek(assigned, now);

    // Not behind — nothing to do
    if (expectedWeek <= currentWeek) return currentWeek;

    // Cap at total weeks + 1 (sentinel for program complete). A client already past the weeks made so far
    // is never moved back by the cap: generating the next week is determineAndSetCurrentWeek's job
    // (Codex's review 045, R2).
    const targetWeek = Math.min(expectedWeek, weeksData.length + 1);
    if (targetWeek <= currentWeek) return currentWeek;

    // For each week that elapsed without full completion, mark unfinished days as missed
    for (let wNum = currentWeek; wNum < targetWeek && wNum <= weeksData.length; wNum++) {
      const week = weeksData.find(w => w.week_number === wNum);
      if (!week?.days?.length) continue;

      const dayIds = week.days.map(d => d.id);

      // Which days of this week are already accounted for in this assignment period? A session row
      // exists as soon as a day is OPENED, so its existence alone proves nothing: a real attempt (a
      // set was completed) must not be shadowed by a duplicate 'missed' row, but a day that was only
      // opened and never trained IS a missed day, and used never to be recorded as one.
      // planMissedDays holds that rule; here we only gather what it needs.
      //
      // If either read fails, stop without advancing the week, so the next load tries again. Reading
      // a failed request as "no sessions" would mark every day of the week missed, done ones included.
      const { data: existingSessions, error: existingErr } = await supabase
        .from('workout_sessions')
        .select('id, day_id, status, started_at, completed_at')
        .eq('user_id', userId)
        .in('day_id', dayIds)
        .gte('started_at', assignedAt);
      if (existingErr) {
        console.error('[autoAdvanceMissedWeeks] could not read sessions, not advancing:', existingErr);
        return currentWeek;
      }
      let trainedOpenIds;
      try {
        trainedOpenIds = await loadTrainedOpenSessionIds(existingSessions || []);
      } catch (logsErr) {
        console.error('[autoAdvanceMissedWeeks] could not read logs, not advancing:', logsErr);
        return currentWeek;
      }

      // Calculate the end-of-week timestamp to use as the missed session date
      const weekEndDate = new Date(assigned);
      weekEndDate.setDate(weekEndDate.getDate() + (wNum - 1) * 7 + 6);
      const missedAt = weekEndDate.toISOString();

      const { convertSessionIds, inserts: missedInserts } = planMissedDays({
        days: week.days, sessions: existingSessions || [], trainedOpenIds, missedAt, userId, programId, now,
      });

      // A failed write here is logged and the week still advances: leaving a client stuck on a past
      // week is worse than one missed day going unrecorded.
      if (convertSessionIds.length > 0) {
        const { error: convertErr } = await supabase
          .from('workout_sessions')
          .update({ status: 'missed', completed_at: missedAt })
          .in('id', convertSessionIds)
          .eq('status', 'in_progress');
        if (convertErr) console.error('[autoAdvanceMissedWeeks] could not mark opened days as missed:', convertErr);
      }
      if (missedInserts.length > 0) {
        const { error: insertErr } = await supabase.from('workout_sessions').insert(missedInserts);
        if (insertErr) console.error('[autoAdvanceMissedWeeks] could not record missed days:', insertErr);
      }
    }

    // Advance current_week in the DB
    await supabase
      .from('program_assignments')
      .update({ current_week: targetWeek, current_week_index: null, current_day_index: null })
      .eq('user_id', userId)
      .eq('is_active', true);

    return targetWeek;
  };

  // OPTIMIZED: Batch fetch completion data
  const fetchCompletionData = async (userId, dayIds, programExercises, assignmentDate) => {
    try {
      const peIds = programExercises.map(pe => pe.id);
      // Guard: empty IN() clauses are invalid in PostgREST — bail out cleanly
      if (!peIds.length || !dayIds.length) return;

      // Query 1: workout_logs — count completed/skipped sets per exercise
      let logsQuery = supabase
        .from('workout_logs')
        .select('program_exercise_id, completed, skipped')
        .eq('user_id', userId)
        .in('program_exercise_id', peIds);

      if (assignmentDate) {
        logsQuery = logsQuery.gte('created_at', assignmentDate);
      }

      // Query 2: completed workout_sessions — a day with completed_at IS the source of truth
      let sessionsQuery = supabase
        .from('workout_sessions')
        .select('day_id')
        .eq('user_id', userId)
        .in('day_id', dayIds)
        .not('completed_at', 'is', null);

      if (assignmentDate) {
        sessionsQuery = sessionsQuery.gte('started_at', assignmentDate);
      }

      // Query 3: missed workout_sessions
      let missedQuery = supabase
        .from('workout_sessions')
        .select('day_id')
        .eq('user_id', userId)
        .in('day_id', dayIds)
        .eq('status', 'missed');
      if (assignmentDate) {
        missedQuery = missedQuery.gte('started_at', assignmentDate);
      }

      const [
        { data: allLogs, error: logsError },
        { data: completedSessions, error: sessionsError },
        { data: missedSessions, error: missedError },
      ] = await Promise.all([logsQuery, sessionsQuery, missedQuery]);

      // A read that failed (no signal, a dropped request) says nothing about what has been done.
      // Treated as "no logs, no completed days" it redrew every day as untouched, every ten
      // seconds, for as long as the connection was down. Keep what is on screen; the next poll
      // tries again.
      if (logsError || sessionsError) return;

      // Days with a completed session are always treated as 100% done
      const completedSessionDayIds = new Set((completedSessions || []).map(s => String(s.day_id)));
      // missedSessions query requires the status column — silently degrade if it's missing
      const missedDayIdSet = missedError ? new Set() : new Set((missedSessions || []).map(s => String(s.day_id)));

      // Count exercises that are either completed OR skipped
      const completedOrSkippedSet = new Set(
        allLogs?.filter(l => l.completed === true || l.skipped === true)
          .map(l => l.program_exercise_id) || []
      );

      // Build completion map
      const completion = {};
      const exercisesByDay = {};

      programExercises.forEach(pe => {
        if (!exercisesByDay[pe.day_id]) exercisesByDay[pe.day_id] = [];
        exercisesByDay[pe.day_id].push(pe.id);
      });

      Object.entries(exercisesByDay).forEach(([dayId, exIds]) => {
        const total = exIds.length;
        const logCompleted = exIds.filter(id => completedOrSkippedSet.has(id)).length;
        const isMissed = missedDayIdSet.has(String(dayId));
        // Missed days contribute 0% completion; completed sessions are treated as fully done
        const completed = isMissed
          ? 0
          : completedSessionDayIds.has(String(dayId)) ? total : logCompleted;
        completion[dayId] = {
          completed,
          total,
          percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        };
      });

      // Only update state if data actually changed to prevent render loops
      setCompletionData(prev => {
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(completion);
        return prevStr === newStr ? prev : completion;
      });

      setMissedDayIds(prev => {
        const prevStr = JSON.stringify([...prev].sort());
        const newStr = JSON.stringify([...missedDayIdSet].sort());
        return prevStr === newStr ? prev : missedDayIdSet;
      });
    } catch (err) {
      toastError('Failed to load completion data.');
    }
  };
  fetchCompletionDataRef.current = fetchCompletionData;

  // 🎯 Use tracked current week from program_assignments
  const determineAndSetCurrentWeek = async (weeksData, userId, programId, dayIds, programExercises, assignmentDate, savedCurrentWeek = null) => {
    try {

      // Use passed value if available, otherwise fall back to DB query
      let currentWeekNumber;
      if (savedCurrentWeek != null) {
        currentWeekNumber = savedCurrentWeek;
      } else {
        const { data: assignmentWeekData } = await supabase
          .from('program_assignments')
          .select('current_week')
          .eq('user_id', userId)
          .eq('program_id', programId)
          .eq('is_active', true)
          .maybeSingle();
        currentWeekNumber = assignmentWeekData?.current_week || 1;
      }

      // Find the week index for this week number
      const targetWeekIndex = weeksData.findIndex(w => w.week_number === currentWeekNumber);

      if (targetWeekIndex === -1) {
        // currentWeekNumber is beyond the last existing week — generate next week
        if (currentWeekNumber > weeksData.length) {
          setGeneratingWeek(true);
          setWeekGenerationFailed(false);
          try {
            await generateNextWeek(programId, weeksData.length);
            await fetchUserAndProgram();
          } catch (err) {
            setWeekGenerationFailed(true);
            setGeneratingWeek(false);
            // Land on last available week so the "Start next week" button is visible
            setSelectedWeekIndex(weeksData.length - 1);
            setSelectedDayIndex(0);
          }
          return;
        }
        setSelectedWeekIndex(0);
        setSelectedDayIndex(0);
        return;
      }

      const currentWeek = weeksData[targetWeekIndex];

      // Use workout_sessions as authoritative source for day completion — a day is only
      // "done" when the user explicitly tapped Complete Day (completed_at is set).
      // Previously used workout_logs which caused premature forward jumps (partial logs
      // looked complete) and backward jumps (missing a log made a done day look incomplete).
      const allDayIds = currentWeek.days?.map(d => d.id) || [];
      let sessionsQuery = supabase
        .from('workout_sessions')
        .select('day_id')
        .eq('user_id', userId)
        .in('day_id', allDayIds)
        .not('completed_at', 'is', null);
      if (assignmentDate) {
        sessionsQuery = sessionsQuery.gte('started_at', assignmentDate);
      }
      const { data: completedSessions } = await sessionsQuery;
      const completedDayIds = new Set((completedSessions || []).map(s => String(s.day_id)));

      // Check if all days in the current week are done
      const allDaysComplete = allDayIds.every(id => completedDayIds.has(String(id)));

      const isLastWeek = currentWeekNumber >= weeksData.length;

      if (allDaysComplete) {
        if (isLastWeek) {
          // Advance current_week in DB then generate the next week
          await supabase
            .from('program_assignments')
            .update({ current_week: weeksData.length + 1 })
            .eq('user_id', userId).eq('program_id', programId).eq('is_active', true);
          setGeneratingWeek(true);
          setWeekGenerationFailed(false);
          try {
            await generateNextWeek(programId, weeksData.length);
            await fetchUserAndProgram();
          } catch (err) {
            setWeekGenerationFailed(true);
            setGeneratingWeek(false);
            // Land on last available week so the "Start next week" button is visible
            setSelectedWeekIndex(weeksData.length - 1);
            setSelectedDayIndex(0);
          }
          return;
        } else {
          // Non-last week: all days complete but current_week not yet advanced (e.g. sessions
          // were manually marked). Advance current_week so the client lands on week N+1.
          const nextWeekNumber = currentWeekNumber + 1;
          await supabase
            .from('program_assignments')
            .update({ current_week: nextWeekNumber, current_week_index: null, current_day_index: null })
            .eq('user_id', userId).eq('program_id', programId).eq('is_active', true);
          const nextWeekIndex = weeksData.findIndex(w => w.week_number === nextWeekNumber);
          if (nextWeekIndex !== -1) {
            setSelectedWeekIndex(nextWeekIndex);
            setSelectedDayIndex(0);
          } else {
            setSelectedWeekIndex(targetWeekIndex);
            setSelectedDayIndex(0);
          }
          return;
        }
      }

      const firstIncompleteDay = currentWeek.days?.findIndex(day =>
        !completedDayIds.has(String(day.id))
      );

      const targetDayIndex = firstIncompleteDay !== -1 ? firstIncompleteDay : 0;

      setSelectedWeekIndex(targetWeekIndex);
      setSelectedDayIndex(targetDayIndex);

    } catch (err) {
      // Default to week 1, day 1 on error
      setSelectedWeekIndex(0);
      setSelectedDayIndex(0);
    }
  };

  useEffect(() => {
    fetchUserAndProgram();
  }, [fetchUserAndProgram]);

  // Re-fetch when page becomes visible again.
  // Short absence (< 5 min, e.g. phone locked between sets): silently refresh completion
  // data only — no loading screen, workout view stays intact.
  // Long absence (≥ 5 min): full reload to catch any coach edits.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // Nothing to refresh from with no connection, and with an expired login each request
      // waits about 25 seconds to fail, ahead of anything the client then tries to do.
      if (!navigator.onLine) return;
      const secondsAway = (Date.now() - lastFullFetchRef.current) / 1000;
      if (secondsAway >= 300) {
        // Away 5+ minutes — do a full silent refresh (no loading spinner)
        // If mid-session, skip re-navigation so the user isn't jumped to a different day
        fetchUserAndProgram(true, !!currentSessionId);
      } else if (userId && program?.id && weeks.length > 0) {
        // Short absence — only refresh completion state, no loading screen
        const dayIds = weeks.flatMap(w => w.days?.map(d => d.id) || []);
        const allExercises = weeks.flatMap(w => w.days?.flatMap(d => d.exercises || []) || []);
        refreshCompletionSoon(userId, dayIds, allExercises, assignedAt);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchUserAndProgram, refreshCompletionSoon, userId, program?.id, weeks, assignedAt, currentSessionId]);

  // 🔄 AUTO-REFRESH using polling (WebSocket real-time is failing)
  useEffect(() => {
    if (!userId || !program?.id || weeks.length === 0) return;

    const runPoll = () => {
      if (document.visibilityState !== 'visible') return;
      if (!navigator.onLine) return; // same reason as the refresh above
      const dayIds = weeks.flatMap(w => w.days?.map(d => d.id) || []);
      const allExercises = weeks.flatMap(w =>
        w.days?.flatMap(d => d.exercises || []) || []
      );
      refreshCompletionSoon(userId, dayIds, allExercises, assignedAt);
    };

    // Poll completion data once a minute (skips when the tab is hidden or there is no connection)
    const completionInterval = setInterval(runPoll, COMPLETION_POLL_MS);

    return () => {
      clearInterval(completionInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, program?.id, weeks]);

  // canComplete — pure derived value, no extra state/useEffect needed
  const canComplete = useMemo(() => {
    const week = weeks[selectedWeekIndex];
    const day = week?.days?.[selectedDayIndex];
    if (!day?.exercises?.length) return false;
    const dayExIds = day.exercises.map(pe => pe.id);
    const hasLocalCompletions = dayExIds.some(id => localSetCompletions.has(id));
    const hasDbProgress = completionData[day.id]?.completed > 0;
    const hasStatuses = Object.keys(exerciseStatuses).length > 0;
    return hasLocalCompletions || hasDbProgress || hasStatuses;
  }, [weeks, selectedWeekIndex, selectedDayIndex, completionData, exerciseStatuses, localSetCompletions]);

  // Check monthly check-in eligibility
  useEffect(() => {
    const checkEligibility = async () => {
      if (!userId || !program?.id) return;

      const eligibility = await checkEligibilityForCheckIn(userId, program.id);

      // Only update if eligibility actually changed
      setCheckInEligibility(prev => {
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(eligibility);
        return prevStr === newStr ? prev : eligibility;
      });

      // Auto-open the modal the first time eligibility is detected for this week. It takes the visit's one
      // pop-up slot, so the add-to-home-screen guide cannot open over it; if the guide already has it, the
      // check-in opens by itself on a later visit (its card stays on this screen meanwhile).
      if (eligibility?.eligible && userId) {
        const promptKey = `cipher_checkin_prompted_${userId}_w${eligibility.nextCheckInWeek}`;
        if (!localStorage.getItem(promptKey) && claimPromptSlot('check-in')) {
          localStorage.setItem(promptKey, '1');
          setShowCheckInModal(true);
        }
      }
    };

    checkEligibility();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, program?.id]); // Removed completionData to reduce frequency

  // Sync any session completions that were queued while offline
  const syncPendingCompletions = useCallback(async () => {
    const pending = await getPendingSessionCompletions();
    for (const item of pending) {
      try {
        const { error } = await supabase
          .from('workout_sessions')
          .update(item.updateData)
          .eq('id', item.sessionId);
        if (!error) await removePendingSessionCompletion(item.id);
      } catch {
        // Keep in queue — retry on next reconnect
      }
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => syncPendingCompletions();
    window.addEventListener('online', handleOnline);
    // Sync any leftovers from a previous offline session
    if (navigator.onLine) syncPendingCompletions();
    return () => window.removeEventListener('online', handleOnline);
  }, [syncPendingCompletions]);

  // Persist current position to program_assignments so it survives page reloads
  const savePosition = useCallback(async (weekIndex, dayIndex) => {
    if (!userId) return;
    try {
      await supabase
        .from('program_assignments')
        .update({ current_week_index: weekIndex, current_day_index: dayIndex })
        .eq('user_id', userId)
        .eq('is_active', true);
    } catch {
      // Non-critical — silently ignore
    }
  }, [userId]);

  // Single navigation function — batches ALL state updates so there is no flicker
  // between a "week index set" and a "day index set" step.
  const navigateTo = useCallback((weekIndex, dayIndex) => {
    setExercisesLoading(true);
    setSelectedWeekIndex(weekIndex);
    setSelectedDayIndex(dayIndex);
    setContentKey(k => k + 1);
    setLocalSetCompletions(new Set());
    setExerciseStatuses({});
    setEnergySubmitted(false);
    setFinishOpen(false);
    savePosition(weekIndex, dayIndex);
    setTimeout(() => setExercisesLoading(false), 300);
  }, [savePosition]);

  // eslint-disable-next-line no-unused-vars
  const handleWeekChange = useCallback((index) => navigateTo(index, 0), [navigateTo]);
  const handleDayChange = useCallback((index) => navigateTo(selectedWeekIndex, index), [navigateTo, selectedWeekIndex]);

  // eslint-disable-next-line no-unused-vars
  const getWeekProgress = (week) => {
    if (!week.days?.length) return 0;
    let total = 0, completed = 0;
    week.days.forEach(day => {
      const dc = completionData[day.id];
      if (dc) { total += dc.total; completed += dc.completed; }
    });
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const isDayMissed = (day) => missedDayIds.has(String(day.id));

  const isDayCompleted = (day) => {
    if (isDayMissed(day)) return false;
    const dc = completionData[day.id];
    return dc && dc.total > 0 && dc.completed >= dc.total;
  };

  const getNextSession = () => {
    // Only navigate within the current week — no cross-week jumping
    const week = weeks[selectedWeekIndex];
    if (!week?.days) return null;
    for (let d = 0; d < week.days.length; d++) {
      if (!isDayCompleted(week.days[d]) && !isDayMissed(week.days[d])) {
        return { weekIndex: selectedWeekIndex, dayIndex: d, day: week.days[d] };
      }
    }
    return null;
  };

  const getProcessedExercises = (exercises) => {
    if (!exercises) return [];
    // Fixed order: exercises come pre-sorted by exercise_order from the database
    // Order convention: Warm-up → Main lifts → Accessories → Conditioning → Cooldown
    let filtered = [...exercises];

    if (exerciseFilter !== 'all') {
      const count = filtered.length;
      if (exerciseFilter === 'warmup') filtered = filtered.slice(0, Math.ceil(count * 0.2));
      else if (exerciseFilter === 'main') filtered = filtered.slice(Math.ceil(count * 0.2), Math.ceil(count * 0.8));
      else if (exerciseFilter === 'finisher') filtered = filtered.slice(Math.ceil(count * 0.8));
    }

    return filtered;
  };

  // Skipped reasons
  const SKIPPED_REASONS = [
    { value: 'pain', label: 'Pain/Injury' },
    { value: 'time', label: 'Time Constraint' },
    { value: 'equipment', label: 'Equipment Unavailable' },
    { value: 'fatigue', label: 'Fatigue' },
    { value: 'other', label: 'Other' },
  ];

  // Load exercise statuses from database for current day
  const loadExerciseStatuses = useCallback(async (dayId, exercises) => {
    if (!userId || !dayId || !exercises?.length) return;

    try {
      const peIds = exercises.map(pe => pe.id);

      // Get workout logs — filter to current assignment period only
      // so re-assigned programs start fresh instead of showing old completions
      let statusQuery = supabase
        .from('workout_logs')
        .select('program_exercise_id, completed, skipped')
        .eq('user_id', userId)
        .in('program_exercise_id', peIds);

      if (assignedAt) {
        statusQuery = statusQuery.gte('created_at', assignedAt);
      }

      const { data: logs } = await statusQuery;

      if (!logs) return;

      const statuses = {};
      const localCompletions = new Set();

      // Group logs by exercise
      const logsByExercise = {};
      logs.forEach(log => {
        if (!logsByExercise[log.program_exercise_id]) {
          logsByExercise[log.program_exercise_id] = [];
        }
        logsByExercise[log.program_exercise_id].push(log);
      });

      // Determine status for each exercise
      exercises.forEach(pe => {
        const exerciseLogs = logsByExercise[pe.id] || [];

        // Check if all sets are completed (exercise is done)
        const numSets = pe.sets || 3;
        const completedSets = exerciseLogs.filter(l => l.completed).length;
        const hasSkipped = exerciseLogs.some(l => l.skipped);

        if (completedSets >= numSets) {
          statuses[pe.id] = { status: 'done', skippedReason: null };
          localCompletions.add(pe.id); // Track in local completions
        } else if (hasSkipped) {
          // If exercise is skipped, mark it in local completions too
          localCompletions.add(pe.id);
        } else if (completedSets > 0) {
          // If any sets are completed (but not all), still track for button reactivity
          localCompletions.add(pe.id);
        }
      });

      setExerciseStatuses(statuses);
      setLocalSetCompletions(localCompletions);
    } catch (error) {
      toastError('Failed to load exercise statuses.');
    }
  }, [userId, assignedAt]);

  // Save the energy chosen on this device on the day's real session. It stays on the device until a save
  // is confirmed, and is retried when the session is resolved, when the device is back online and when the
  // day is opened again (Codex's review of PR 116, M1)
  const flushingEnergy = useRef(new Set());
  const flushPendingEnergy = useCallback(async (dayId, sessionId) => {
    if (!userId || !program?.id || !dayId || !sessionId || isOfflineSessionId(sessionId)) return;
    const key = workoutKey(userId, program.id, dayId);
    const pending = pendingEnergy(key);
    if (!pending || flushingEnergy.current.has(key)) return;
    flushingEnergy.current.add(key);
    try {
      const { data, error } = await supabase
        .from('workout_sessions')
        .update({ energy_rating: pending.rating })
        .eq('id', sessionId)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('energy rating not saved: no session row');
      dropPendingEnergy(key, pending.at);
      setSavedEnergy(prev => (prev.dayId === dayId ? { dayId, sessionId, rating: pending.rating } : prev));
    } catch (e) {
      console.error('[Session] energy rating not saved yet:', e);
      if (navigator.onLine && !energyToasted.current.has(key)) {
        energyToasted.current.add(key);
        toastError("Your energy rating hasn't saved yet. It will try again.");
      }
    } finally {
      flushingEnergy.current.delete(key);
    }
  }, [userId, program?.id]);

  // The exercises found or created this day's session (or its real one, after offline use)
  // This is the day's session from now on: energy and completion go to it, and a day check answered later
  // cannot replace it (Codex's re-check of PR 116, R2)
  const readingEnergyOf = useRef(null);
  const handleSessionCreated = (sessionId, dayId) => {
    // A day no longer on screen: its energy is still saved, nothing on screen changes
    if (dayId !== currentDayIdRef.current) { flushPendingEnergy(dayId, sessionId); return; }
    loggerSession.current = { dayId, sessionId };
    setCurrentSessionId(sessionId);
    setSessionLocked(false);
    flushPendingEnergy(dayId, sessionId);
    if (isOfflineSessionId(sessionId)) return;
    // Its own energy, read once, unless the day check already read this very session
    const known = savedEnergyRef.current;
    const which = `${dayId}:${sessionId}`;
    if ((known.dayId === dayId && known.sessionId === sessionId) || readingEnergyOf.current === which) return;
    readingEnergyOf.current = which;
    supabase
      .from('workout_sessions')
      .select('energy_rating')
      .eq('id', sessionId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) throw error;
        if (loggerSession.current.dayId !== dayId || loggerSession.current.sessionId !== sessionId) return;
        setSavedEnergy({ dayId, sessionId, rating: data?.energy_rating ?? null });
      })
      .catch((e) => console.error('[Session] energy of the session not read:', e))
      .finally(() => { if (readingEnergyOf.current === which) readingEnergyOf.current = null; });
  };

  useEffect(() => {
    const retry = () => flushPendingEnergy(loggerSession.current.dayId, loggerSession.current.sessionId);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [flushPendingEnergy]);

  // 🔄 Handle set completion - track locally for instant UI updates
  const handleSetCompleted = (peId) => {

    // Update local completion tracker immediately (no database query needed!)
    setLocalSetCompletions(prev => {
      const updated = new Set(prev);
      updated.add(peId);
      return updated;
    });

    // Still refresh completion data in background for accuracy (but don't wait). Every exercise
    // on the day calls this on every save, so it goes through the gate.
    const dayIds = weeks.flatMap(w => w.days?.map(d => d.id) || []);
    const allExercises = weeks.flatMap(w =>
      w.days?.flatMap(d => d.exercises || []) || []
    );
    refreshCompletionSoon(userId, dayIds, allExercises, assignedAt);
  };

  // Handle exercise status change (from WorkoutLogger) - persists to database
  const handleExerciseStatusChange = async (peId, status, skippedReason = null) => {
    // Update local state immediately for responsive UI
    setExerciseStatuses(prev => {
      const updated = {
        ...prev,
        [peId]: { status, skippedReason },
      };
      return updated;
    });

    // Also track in local completions for instant button reactivity
    if (status === 'done' || status === 'skipped') {
      setLocalSetCompletions(prev => {
        const updated = new Set(prev);
        updated.add(peId);
        return updated;
      });
    }

    // Persist to database if we have a session
    if (currentSessionId && userId) {
      try {
        const exercise = currentDay?.exercises?.find(pe => pe.id === peId);
        if (!exercise) return;

        if (status === 'skipped') {
          // Save skipped status - real-time subscription will handle UI update

          // Check if a log already exists for this exercise
          const { data: existingLog } = await supabase
            .from('workout_logs')
            .select('id')
            .eq('session_id', currentSessionId)
            .eq('program_exercise_id', peId)
            .eq('set_number', 1)
            .maybeSingle();

          if (existingLog) {
            // Update existing log
            await supabase
              .from('workout_logs')
              .update({
                skipped: true,
                skipped_reason: skippedReason,
                completed: false,
              })
              .eq('id', existingLog.id);
          } else {
            // Insert new log
            await supabase
              .from('workout_logs')
              .insert({
                session_id: currentSessionId,
                user_id: userId,
                program_exercise_id: peId,
                exercise_id: exercise.exercise_id,
                set_number: 1,
                skipped: true,
                skipped_reason: skippedReason,
                completed: false,
              });
          }
        } else if (status === null) {
          // Clear skipped status if unmarking
          await supabase
            .from('workout_logs')
            .update({ skipped: false, skipped_reason: null })
            .eq('session_id', currentSessionId)
            .eq('program_exercise_id', peId)
            .eq('skipped', true);
        }
        // 'done' status is handled by WorkoutLogger when all sets are completed
        // Real-time subscription will automatically update completion data!

        // Refresh completion data immediately
        await handleSetCompleted();

      } catch (error) {
        toastError('Failed to update exercise status. Please try again.');
      }
    }
  };

  // Check if user can end the session (legacy function - now using canComplete state)
  // eslint-disable-next-line no-unused-vars
  const canCompleteDay = () => {
    if (!currentDay?.exercises?.length) return false;
    const dayCompletion = completionData[currentDay.id];
    const hasAnyProgress = dayCompletion && dayCompletion.completed > 0;
    return hasAnyProgress || Object.keys(exerciseStatuses).length > 0;
  };

  // Complete the day
  // rpe: 2-10 from the finish screen, or none. One completion at a time: the guard is taken before the
  // first wait, so a second tap while the sets are checked does nothing (Codex's review of PR 116, M3)
  const handleCompleteDay = async (rpe = null) => {
    if (completingRef.current) return;
    completingRef.current = true;
    setDayCompleting(true);
    try {
      await completeDay(rpe);
    } finally {
      completingRef.current = false;
      setDayCompleting(false);
    }
  };

  const completeDay = async (rpe) => {

    if (!currentDay || !currentSessionId) {
      toastError('Session not ready. Please refresh and try again.');
      return;
    }

    // Require at least one completed log — use local state when offline to avoid network call
    if (!navigator.onLine) {
      if (localSetCompletions.size === 0) {
        toastError('Log at least one set before completing the day.');
        return;
      }
    } else {
      // An exercise the user marked "Done" (e.g. cardio, where the set may not
      // have a completed=true row yet) still counts — the reconciliation step
      // below writes its completed log before the session is finalised.
      const anyLocalDone = (currentDay.exercises || []).some(
        pe => exerciseStatuses[pe.id]?.status === 'done'
      );

      if (!anyLocalDone) {
        const { data: logCheck } = await supabase
          .from('workout_logs')
          .select('id')
          .eq('session_id', currentSessionId)
          .eq('completed', true)
          .limit(1);

        if (!logCheck || logCheck.length === 0) {
          toastError('Log at least one set before completing the day.');
          return;
        }
      }
    }

    setDayCompleting(true);

    try {
      // Calculate summary data
      const exercises = currentDay.exercises || [];
      const completedExercises = [];
      const skippedExercises = [];

      exercises.forEach(pe => {
        const status = exerciseStatuses[pe.id];
        if (status?.status === 'skipped') {
          skippedExercises.push({
            name: pe.exercise?.name || 'Unknown',
            reason: status.skippedReason,
          });
        } else {
          completedExercises.push(pe.exercise?.name || 'Unknown');
        }
      });

      // Update the workout session as completed
      const updateData = {
        completed_at: new Date().toISOString(),
        status: 'completed',
        notes: skippedExercises.length > 0
          ? `Skipped: ${skippedExercises.map(s => `${s.name} (${s.reason})`).join(', ')}`
          : null,
      };

      // Only add rpe if it has a value
      if (typeof rpe === 'number') {
        updateData.rpe = rpe;
      }

      // ── Offline path: queue and update UI optimistically ──
      if (!navigator.onLine) {
        await queueSessionComplete(currentSessionId, updateData);
        setSessionLocked(true);
        setLocalSetCompletions(new Set());
        setDayCompleting(false);
        toastSuccess('Day completed — will sync when back online');
        markWorkoutCompleted();
        return;
      }

      // Only a session still open is completed, and a row must come back: a refused or already-completed
      // session is not reported as saved
      const { data: completedRows, error: sessionError } = await supabase
        .from('workout_sessions')
        .update(updateData)
        .eq('id', currentSessionId)
        .is('completed_at', null)
        .select('id');

      if (sessionError) throw sessionError;
      if (!completedRows?.length) {
        const { data: already } = await supabase
          .from('workout_sessions')
          .select('completed_at')
          .eq('id', currentSessionId)
          .maybeSingle();
        if (!already?.completed_at) throw new Error('session not completed: no row');
        // Completed already, in another tab or on another device
        setSessionLocked(true);
        setLocalSetCompletions(new Set());
        toastSuccess('This day was already completed.');
        const dayIds = weeks.flatMap(w => w.days?.map(d => d.id) || []);
        const allExercises = weeks.flatMap(w => w.days?.flatMap(d => d.exercises || []) || []);
        fetchCompletionData(userId, dayIds, allExercises, assignedAt).catch(() => {});
        return;
      }

      // ── Session is saved — lock immediately to prevent double-submit ──
      setSessionLocked(true);
      markWorkoutCompleted();

      // ── Best-effort: write exercise status logs ──
      // Day is already complete in the DB; failures here don't undo that.
      let logWritesFailed = false;
      try {
        // Mark skipped exercises in workout_logs
        for (const pe of exercises) {
          const status = exerciseStatuses[pe.id];
          if (status?.status === 'skipped') {
            const { data: existingLog } = await supabase
              .from('workout_logs')
              .select('id')
              .eq('session_id', currentSessionId)
              .eq('program_exercise_id', pe.id)
              .eq('set_number', 1)
              .maybeSingle();

            if (existingLog) {
              await supabase
                .from('workout_logs')
                .update({
                  skipped: true,
                  skipped_reason: status.skippedReason,
                  completed: false,
                })
                .eq('id', existingLog.id);
            } else {
              await supabase
                .from('workout_logs')
                .insert({
                  session_id: currentSessionId,
                  user_id: userId,
                  program_exercise_id: pe.id,
                  exercise_id: pe.exercise_id,
                  set_number: 1,
                  skipped: true,
                  skipped_reason: status.skippedReason,
                  completed: false,
                });
            }
          }
        }

        // Ensure exercises marked as 'done' have at least one completed log in the DB
        // (handles cases where user clicked Done without logging individual sets)
        for (const pe of exercises) {
          const status = exerciseStatuses[pe.id];
          if (status?.status === 'done') {
            const { data: existingLogs } = await supabase
              .from('workout_logs')
              .select('id')
              .eq('session_id', currentSessionId)
              .eq('program_exercise_id', pe.id)
              .eq('completed', true)
              .limit(1);

            if (!existingLogs?.length) {
              await supabase.from('workout_logs').insert({
                session_id: currentSessionId,
                user_id: userId,
                program_exercise_id: pe.id,
                exercise_id: pe.exercise_id,
                set_number: 1,
                completed: true,
              });
            }
          }
        }
      } catch (logErr) {
        console.error('Exercise log writes failed:', logErr);
        logWritesFailed = true;
      }

      // ── Refresh completion data (best-effort) ──
      try {
        const dayIds = weeks.flatMap(w => w.days?.map(d => d.id) || []);
        const allExercises = weeks.flatMap(w =>
          w.days?.flatMap(d => d.exercises || []) || []
        );
        await fetchCompletionData(userId, dayIds, allExercises, assignedAt);
      } catch (refreshErr) {
        console.error('Completion data refresh failed:', refreshErr);
      }

      // Clear local completions since day is done
      setLocalSetCompletions(new Set());

      // Check if this was the last day in the week — auto-trigger progression
      const { data: completedSessionRows } = await supabase
        .from('workout_sessions')
        .select('day_id')
        .eq('user_id', userId)
        .eq('program_id', program.id)
        .in('day_id', (currentWeek?.days ?? []).map(d => d.id))
        .not('completed_at', 'is', null)
        .gte('started_at', assignedAt || '2000-01-01');

      const completedDayIdSet = new Set(completedSessionRows?.map(s => s.day_id) || []);
      const allWeekDaysComplete = (currentWeek?.days ?? []).every(d => completedDayIdSet.has(d.id));

      if (allWeekDaysComplete) {
        try {
          const nextWeekNumber = currentWeek.week_number + 1;
          const nextWeekExists = weeks.some(w => w.week_number === nextWeekNumber);

          // Advance current_week and clear saved position
          await supabase
            .from('program_assignments')
            .update({ current_week: nextWeekNumber, current_week_index: null, current_day_index: null })
            .eq('user_id', userId)
            .eq('is_active', true);

          if (nextWeekExists) {
            toastSuccess(`Week ${currentWeek.week_number} complete! 🎉 Week ${nextWeekNumber} is ready.`);
          } else {
            // Generate the next week immediately so it exists on the next page load
            try {
              setGeneratingWeek(true);
              setWeekGenerationFailed(false);
              await generateNextWeek(program.id, weeks.length);
              await fetchUserAndProgram();
              toastSuccess(`Week ${currentWeek.week_number} complete! 🎉 Week ${nextWeekNumber} is ready.`);
            } catch (genErr) {
              console.error('Week generation failed:', genErr);
              setWeekGenerationFailed(true);
            } finally {
              setGeneratingWeek(false);
            }
          }
        } catch (err) {
          console.error('Week advance failed:', err);
          // Non-critical — day is still marked complete
        }
      }

      // Small delay to ensure database updates propagate
      await new Promise(resolve => setTimeout(resolve, 500));

      // Force component re-render
      setContentKey(k => k + 1);

      // Show workout summary modal
      setCompletedSessionId(currentSessionId);
      setShowSummaryModal(true);

      // Show result toast — week toast already shown above if week was completed
      if (logWritesFailed) {
        toastError('Day saved, but some exercise logs may be incomplete.');
      } else if (!allWeekDaysComplete) {
        toastSuccess('Day completed successfully!');
      }

    } catch (error) {
      // Only reaches here if the session update itself failed — safe to retry
      toastError('Failed to complete day. Please try again.');
    } finally {
      setDayCompleting(false);
    }
  };

  // Mark the current day as missed (skipped the entire session)
  const handleMarkDayMissed = async () => {
    if (!currentDay) return;
    setDayCompleting(true);
    try {
      const now = new Date().toISOString();
      const missedData = { status: 'missed', completed_at: now };

      if (currentSessionId) {
        // Session already exists — update it
        await supabase.from('workout_sessions').update(missedData).eq('id', currentSessionId);
      } else {
        // No session started — create one
        const { data: newSession } = await supabase.from('workout_sessions').insert({
          user_id: userId,
          program_id: program?.id,
          day_id: currentDay.id,
          started_at: now,
          ...missedData,
        }).select('id').single();
        setCurrentSessionId(newSession.id);
      }

      setSessionLocked(true);
      setMissedDayIds(prev => new Set([...prev, String(currentDay.id)]));

      // Auto-resolve any exercise flags for this day's exercises — individual flags
      // are redundant when the whole day is skipped; the missed session captures it.
      const dayExerciseIds = (currentDay.exercises || []).map(pe => pe.id);
      if (dayExerciseIds.length > 0) {
        await supabase
          .from('exercise_flags')
          .update({ resolved_at: now, resolution_notes: 'Day marked as missed' })
          .eq('user_id', userId)
          .in('program_exercise_id', dayExerciseIds)
          .is('resolved_at', null);
      }

      // Check if all days in the week are now accounted for (completed or missed)
      const { data: completedSessionRows } = await supabase
        .from('workout_sessions')
        .select('day_id')
        .eq('user_id', userId)
        .eq('program_id', program.id)
        .in('day_id', (currentWeek?.days ?? []).map(d => d.id))
        .not('completed_at', 'is', null)
        .gte('started_at', assignedAt || '2000-01-01');

      const accountedDayIds = new Set(completedSessionRows?.map(s => s.day_id) || []);
      const allWeekDaysAccounted = (currentWeek?.days ?? []).every(d => accountedDayIds.has(d.id));

      if (allWeekDaysAccounted) {
        const nextWeekNumber = currentWeek.week_number + 1;
        const nextWeekExists = weeks.some(w => w.week_number === nextWeekNumber);
        await supabase
          .from('program_assignments')
          .update({ current_week: nextWeekNumber, current_week_index: null, current_day_index: null })
          .eq('user_id', userId)
          .eq('is_active', true);
        if (nextWeekExists) {
          toastSuccess(`Week ${currentWeek.week_number} recorded. Week ${nextWeekNumber} is ready.`);
        } else {
          // Generate the next week immediately so it exists on the next page load
          try {
            setGeneratingWeek(true);
            setWeekGenerationFailed(false);
            await generateNextWeek(program.id, weeks.length);
            toastSuccess(`Week ${currentWeek.week_number} recorded. Week ${nextWeekNumber} is ready.`);
          } catch (genErr) {
            console.error('Week generation failed:', genErr);
            setWeekGenerationFailed(true);
          } finally {
            setGeneratingWeek(false);
          }
        }
      } else {
        toastSuccess('Day marked as missed.');
      }

      // Refresh completion data
      const dayIds = weeks.flatMap(w => w.days?.map(d => d.id) || []);
      const allExercises = weeks.flatMap(w => w.days?.flatMap(d => d.exercises || []) || []);
      await fetchCompletionData(userId, dayIds, allExercises, assignedAt);
    } catch (err) {
      toastError('Failed to mark day as missed.');
    } finally {
      setDayCompleting(false);
    }
  };

  // Check session lock status when day changes
  const checkSessionLock = useCallback(async (dayId, programId, assignmentDate) => {
    const mine = ++lockRequest.current;
    if (!userId || !dayId) return;


    try {
      // First: look for an ACTIVE (uncompleted) session for this program+day
      // This prevents old completed sessions from falsely locking the day
      let query = supabase
        .from('workout_sessions')
        .select('id, completed_at, energy_rating, started_at')
        .eq('user_id', userId)
        .eq('day_id', dayId);

      // Filter by program_id if provided to avoid matching old sessions from same day
      if (programId) {
        query = query.eq('program_id', programId);
      }

      // Only consider sessions from the current assignment period
      if (assignmentDate) {
        query = query.gte('started_at', assignmentDate);
      }

      const { data: sessions, error: sessionsError } = await query
        .order('started_at', { ascending: false })
        .limit(5);
      if (sessionsError) throw sessionsError;
      if (mine !== lockRequest.current) return;

      // The exercises have already resolved this day's session: it decides. Only a newer answer about that
      // same session is taken (its energy, or that it was completed elsewhere); an older one is ignored
      const resolved = loggerSession.current.dayId === dayId && !isOfflineSessionId(loggerSession.current.sessionId)
        ? loggerSession.current.sessionId : null;
      if (resolved) {
        const same = (sessions || []).find(s => s.id === resolved);
        if (!same) return;
        setSavedEnergy({ dayId, sessionId: same.id, rating: same.energy_rating ?? null });
        setCurrentSessionId(same.id);
        setSessionLocked(!!same.completed_at);
        return;
      }


      if (!sessions || sessions.length === 0) {
        setSavedEnergy({ dayId, sessionId: null, rating: null });
        setCurrentSessionId(null);
        setSessionLocked(false);
        return;
      }

      // Prefer an active (uncompleted) session — never lock if one exists
      const activeSession = sessions.find(s => !s.completed_at);
      if (activeSession) {
        // Its energy counts only if the exercises will resume it; an older open session is replaced by a new one
        const resumable = new Date(activeSession.started_at).getTime() >= Date.now() - RESUMABLE_SESSION_WINDOW_MS;
        setSavedEnergy(resumable
          ? { dayId, sessionId: activeSession.id, rating: activeSession.energy_rating ?? null }
          : { dayId, sessionId: null, rating: null });
        setCurrentSessionId(activeSession.id);
        setSessionLocked(false);
        return;
      }

      // No active session — completed session found, lock immediately
      const mostRecent = sessions[0];
      setSavedEnergy({ dayId, sessionId: mostRecent.id, rating: mostRecent.energy_rating ?? null });
      setCurrentSessionId(mostRecent.id);
      setSessionLocked(true);
    } catch (error) {
      if (mine !== lockRequest.current) return;
      // Not known (offline, or the check failed): ask, and keep the answer on the device until it saves
      setSavedEnergy({ dayId, sessionId: null, rating: null });
      toastError('Failed to check session status.');
    }
  }, [userId]);

  // Load exercise statuses when day changes
  useEffect(() => {
    const currentWeek = weeks[selectedWeekIndex];
    const day = currentWeek?.days?.[selectedDayIndex];
    if (day?.id) {
      setExerciseStatuses({});
      checkSessionLock(day.id, program?.id, assignedAt);
      // Load persisted exercise statuses from database
      loadExerciseStatuses(day.id, day.exercises);
    }
  }, [weeks, selectedWeekIndex, selectedDayIndex, checkSessionLock, loadExerciseStatuses, program?.id, assignedAt]);

  useEffect(() => { if (sessionLocked) setFinishOpen(false); }, [sessionLocked]);

  // weekCompleted — pure derived value, computed once per render, no extra state
  // A week is complete if every day is either properly completed or marked as missed
  const weekCompleted = useMemo(() => {
    const week = weeks[selectedWeekIndex];
    if (!week?.days?.length) return false;
    return week.days.every(day => isDayCompleted(day) || isDayMissed(day));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, selectedWeekIndex, completionData, missedDayIds]);

  // True when viewing the last week that currently exists — next week may need to be generated
  const isOnLastAvailableWeek = selectedWeekIndex === weeks.length - 1;

  // A finished-looking week with a next week already there: one tap closes any day whose sets are all
  // logged but whose "Complete Day" was never recorded, and moves on (openSessionsToClose; 17 October
  // 2026, a client stuck on week 7). The client's own tap, so nothing is changed behind their back.
  const [movingOn, setMovingOn] = useState(false);
  const finishWeekAndMoveOn = async () => {
    const week = weeks[selectedWeekIndex];
    const next = weeks[selectedWeekIndex + 1];
    if (!week || !next || !userId) return;
    setMovingOn(true);
    try {
      const dayIds = week.days.map((d) => d.id);
      let q = supabase.from('workout_sessions').select('id, day_id, started_at, completed_at')
        .eq('user_id', userId).in('day_id', dayIds);
      if (assignedAt) q = q.gte('started_at', assignedAt);
      const { data: sessions, error: readErr } = await q;
      if (readErr) throw readErr;
      const toClose = openSessionsToClose({
        doneDayIds: week.days.filter((d) => isDayCompleted(d) && !isDayMissed(d)).map((d) => d.id),
        sessions,
      });
      if (toClose.length > 0) {
        const { data: closed, error: closeErr } = await supabase.from('workout_sessions')
          .update({ completed_at: new Date().toISOString(), status: 'completed' })
          .in('id', toClose).is('completed_at', null).select('id');
        if (closeErr) throw closeErr;
        if (!closed || closed.length === 0) throw new Error('the day could not be closed');
      }
      const { data: moved, error: moveErr } = await supabase.from('program_assignments')
        .update({ current_week: next.week_number, current_week_index: null, current_day_index: null })
        .eq('user_id', userId).eq('is_active', true).select('id');
      if (moveErr) throw moveErr;
      if (!moved || moved.length === 0) throw new Error('the week could not be moved on');
      await fetchUserAndProgram();
    } catch (err) {
      console.error('[finishWeekAndMoveOn]', err);
      Sentry.captureException(err, { extra: { fn: 'finishWeekAndMoveOn' } });
      toastError('Could not move on to the next week. Check your connection and try again.');
    } finally {
      setMovingOn(false);
    }
  };

  const nextSession = getNextSession();

  // CSS animations
  const cssAnimations = `
    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
    @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

    .skeleton {
      background: linear-gradient(90deg, ${colors.surface2} 25%, ${colors.surface3} 50%, ${colors.surface2} 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    .fade-in { animation: fadeIn 0.3s ease-out forwards; }
    .slide-in { animation: slideIn 0.25s ease-out forwards; }

    .week-tab { transition: all 0.2s ease; }
    .week-tab:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.4); }
    .week-tab:active { transform: translateY(0); }

    .day-card { transition: all 0.18s ease; }
    .day-card:hover { transform: translateX(3px); box-shadow: 0 4px 16px rgba(0,0,0,0.3); }
    .day-card:active { transform: translateX(0); }

    @keyframes chipPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.4); } 50% { box-shadow: 0 0 0 6px rgba(212,175,55,0); } }

    .filter-btn { transition: all 0.15s ease; }
    .filter-btn:hover { background-color: ${colors.surface3} !important; }

    .content-animate { animation: fadeIn 0.35s ease-out; }

    @media (min-width: 900px) {
      .split-layout { display: grid !important; grid-template-columns: 280px 1fr !important; gap: 24px !important; }
      .nav-sidebar { position: sticky !important; top: 20px !important; height: fit-content !important; max-height: calc(100vh - 40px) !important; overflow-y: auto !important; }
      .week-selector-desktop, .day-selector-desktop { flex-direction: column !important; overflow-x: visible !important; }
    }
    @media (max-width: 899px) {
      .sticky-header { position: static !important; margin-bottom: 20px !important; }
      .sticky-selectors { position: static !important; padding-bottom: 16px !important; }
    }
  `;

  // Loading state
  if (loading) {
    return (
      <>
        <style>{cssAnimations}</style>
        <LoadingScreen />
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
        <style>{cssAnimations}</style>
        <div style={styles.stateContainer}>
          <div style={styles.illustrationContainer}>
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="36" stroke={colors.surface3} strokeWidth="2" strokeDasharray="4 4"/>
              <circle cx="40" cy="40" r="24" fill={colors.surface2}/>
              <path d="M40 28v16M40 48v4" stroke={colors.error} strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 style={{ ...typography.h2, color: colors.text, margin: '20px 0 8px' }}>Something went wrong</h2>
          <p style={{ ...typography.body, color: colors.textMuted, margin: '0 0 24px', maxWidth: '280px', textAlign: 'center' }}>{error}</p>
          <div style={styles.actionButtons}>
            <button onClick={() => fetchUserAndProgram()} style={styles.primaryButton}>Try Again</button>
            <button style={styles.secondaryButton}>Contact Support</button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!program) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
        <style>{cssAnimations}</style>
        <PWAInstallGuide role={guideRole} />
        {/* A payment or access notice shows without a program too (Codex's review 039) */}
        {topNotice && <div style={{ padding: '16px 16px 0', maxWidth: '600px', margin: '0 auto' }}>{topNotice}</div>}
        <div style={styles.stateContainer}>
          <div style={styles.illustrationContainer}>
            <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
              <rect x="20" y="15" width="60" height="70" rx="8" stroke={colors.surface3} strokeWidth="2"/>
              <rect x="28" y="28" width="30" height="4" rx="2" fill={colors.surface3}/>
              <rect x="28" y="38" width="44" height="3" rx="1.5" fill={colors.surface2}/>
              <rect x="28" y="46" width="38" height="3" rx="1.5" fill={colors.surface2}/>
              <rect x="28" y="54" width="42" height="3" rx="1.5" fill={colors.surface2}/>
              <circle cx="70" cy="70" r="20" fill={colors.accentGlow}/>
              <path d="M65 70h10M70 65v10" stroke={colors.accent} strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 style={{ ...typography.h2, color: colors.text, margin: '20px 0 8px' }}>No Program Yet</h2>
          <p style={{ ...typography.body, color: colors.textMuted, margin: '0 0 24px', maxWidth: '300px', textAlign: 'center' }}>
            Your trainer hasn't assigned you a program yet. Check back soon or reach out to get started.
          </p>
          <button onClick={() => fetchUserAndProgram()} style={styles.primaryButton}>Refresh</button>
        </div>
      </div>
    );
  }

  // programComplete no longer locks the client out — handled by banner in the main view

  const currentWeek = weeks[selectedWeekIndex];
  const currentDay = currentWeek?.days?.[selectedDayIndex];
  const processedExercises = getProcessedExercises(currentDay?.exercises);
  const duration = estimateDuration(currentDay?.exercises);
  const primaryMuscle = getPrimaryMuscle(currentDay?.exercises);
  const dayCompletion = completionData[currentDay?.id] || { completed: 0, total: 0, percentage: 0 };
  const weekDaysForProgress = currentWeek?.days || [];
  const weeklySessionsDone = weekDaysForProgress.filter(isDayCompleted).length;
  // Before the week closes, say so: a missed day is a true record and is not reopened (owner, 25 October 2026)
  const weekReminder = weekEndingSoon({
    clock: weekClock,
    week: currentWeek?.week_number,
    workoutsLeft: weekDaysForProgress.filter((d) => !isDayCompleted(d) && !isDayMissed(d)).length,
  });
  const weeklySessionsTotal = weekDaysForProgress.length;
  const firstName = (userName || '').split(' ')[0];
  // Before a session: the energy question starts it. Only for a day with nothing logged, no energy saved,
  // and not already started without one; the exercises load underneath as before, so when a session
  // starts and how it is counted are unchanged
  currentDayIdRef.current = currentDay?.id ?? null;
  const thisWorkout = currentDay && userId && program?.id ? workoutKey(userId, program.id, currentDay.id) : null;
  const showSessionStart = !!thisWorkout && !sessionLocked && !energySubmitted
    && savedEnergy.dayId === currentDay.id && savedEnergy.rating == null
    && dayCompletion.completed === 0 && localSetCompletions.size === 0
    && processedExercises.length > 0 && !startedWithoutRating(thisWorkout) && !pendingEnergy(thisWorkout);
  // The answer is kept on the device first, then saved on the session as soon as there is one
  const beginSession = (rating) => {
    keepPendingEnergy(thisWorkout, rating);
    setEnergySubmitted(true);
    setPendingTick(t => t + 1);
    if (loggerSession.current.dayId === currentDay.id) flushPendingEnergy(currentDay.id, loggerSession.current.sessionId);
  };
  const startWithoutRating = () => {
    rememberStartedWithoutRating(thisWorkout);
    setEnergySubmitted(true);
  };

  return (
    <div style={styles.container}>
      <PWAInstallGuide role={guideRole} ready={checkInEligibility !== null} blocked={!showSessionStart || showCheckInModal || finishOpen} />
      <style>{cssAnimations}</style>

      {/* Program complete banner — non-blocking */}
      {programComplete && (
        <div style={{
          margin: '12px 16px 0',
          padding: '14px 16px',
          background: `linear-gradient(135deg, ${shared.surface1}, ${shared.surface2})`,
          border: `1px solid ${colors.accent}`,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <svg width="28" height="28" viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="50" cy="50" r="44" fill={colors.accentGlow} stroke={colors.accent} strokeWidth="3"/>
            <path d="M30 50l14 14 26-26" stroke={colors.accent} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div style={{ flex: 1 }}>
            <p style={{ ...typography.bodyBold, color: colors.accent, margin: 0 }}>Program Complete!</p>
            <p style={{ ...typography.small, color: colors.textMuted, margin: '2px 0 0' }}>
              All {weeks.length} weeks done. Keep training below while your coach assigns your next program.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky-header" style={styles.header}>
        {/* Live dot — always shown when program is loaded. Decoration only: clicks pass through it. Placed
            in the header's corner (it used to sit over the computer top bar's Logout button) */}
        <div aria-hidden="true" style={{
          pointerEvents: 'none',
          position: 'absolute',
          top: '12px',
          right: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          backgroundColor: 'rgba(52, 199, 89, 0.12)',
          borderRadius: '20px',
          fontSize: '11px',
          color: shared.success,
          fontWeight: '600',
          letterSpacing: '0.5px',
        }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: shared.success,
            animation: 'pulse 2s ease-in-out infinite',
          }} />
          LIVE
        </div>
        <div style={styles.headerTop}>
          <div style={styles.headerLeft}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <img src={cipherLogo} alt="Cipher" style={{ width: '48px', height: '48px', flexShrink: 0 }} />
              <div style={{
                color: shared.gold, fontSize: '15px', fontWeight: 700,
                letterSpacing: '3px', fontFamily: "'Orbitron', sans-serif",
                textShadow: '0 0 20px rgba(212, 175, 55, 0.3)',
              }}>
                CIPHER
              </div>
            </div>
            <h1 style={{ ...typography.h1, color: colors.text, margin: 0, fontSize: '20px' }}>{program.name}</h1>
            {program.description && (
              <div style={{ margin: '4px 0 0' }}>
                <p style={{
                  ...typography.body, color: colors.textMuted, margin: 0,
                  fontSize: '13px', lineHeight: '1.4',
                  ...(descExpanded ? {} : {
                    overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }),
                }}>{program.description}</p>
                <button
                  onClick={() => setDescExpanded(v => !v)}
                  style={{
                    background: 'none', border: 'none', padding: '4px 0 0',
                    color: colors.accent, fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                  }}
                >
                  {descExpanded ? 'Show less' : 'Read more'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Something the client should know before anything else, e.g. that their access is about to end */}
        {topNotice}

        {/* Coach message banner — standalone notes from coach_notes, shown one at a time */}
        {unreadCoachNotes.length > 0 && (
          <div style={{
            margin: '0 0 12px',
            padding: '14px 16px',
            backgroundColor: 'rgba(212,175,55,0.08)',
            border: '1px solid rgba(212,175,55,0.3)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>✉️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: colors.accent, fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '4px' }}>
                MESSAGE FROM YOUR COACH{unreadCoachNotes.length > 1 ? ` (1 of ${unreadCoachNotes.length})` : ''}
              </div>
              <div style={{ color: colors.text, fontSize: '14px', lineHeight: 1.5 }}>
                {unreadCoachNotes[0].message}
              </div>
            </div>
            <button
              onClick={() => {
                const note = unreadCoachNotes[0];
                markCoachNoteRead(note.id).catch(() => {});
                setUnreadCoachNotes(prev => prev.filter(n => n.id !== note.id));
              }}
              style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '18px', flexShrink: 0, padding: '0 2px' }}
            >
              ×
            </button>
          </div>
        )}

        {/* Coach session note banner */}
        {coachSessionNote && coachNoteVisible && (
          <div style={{
            margin: '0 0 12px',
            padding: '14px 16px',
            backgroundColor: 'rgba(52,199,89,0.08)',
            border: '1px solid rgba(52,199,89,0.25)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>💬</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: shared.success, fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '4px' }}>
                COACH FEEDBACK
              </div>
              <div style={{ color: colors.text, fontSize: '14px', lineHeight: 1.5 }}>
                {coachSessionNote.note}
              </div>
            </div>
            <button
              onClick={() => {
                localStorage.setItem(`cipher_coach_note_seen_${coachSessionNote.sessionId}`, '1');
                setCoachNoteVisible(false);
              }}
              style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '18px', flexShrink: 0, padding: '0 2px' }}
            >
              ×
            </button>
          </div>
        )}

        {/* Monthly Check-In Banner */}
        {checkInEligibility?.eligible && (
          <div
            style={styles.checkInBanner}
            onClick={() => setShowCheckInModal(true)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div>
                <div style={{ ...typography.small, color: colors.bg, fontWeight: '600' }}>
                  Monthly Check-In Available!
                </div>
                <div style={{ ...typography.tiny, color: colors.bg, opacity: 0.8 }}>
                  {checkInBannerText(checkInEligibility)}
                </div>
              </div>
            </div>
            <button style={styles.checkInButton}>
              Check In →
            </button>
          </div>
        )}

      </div>

      {/* Split Layout */}
      <div className="split-layout" style={styles.splitLayout}>
        {/* Navigation Sidebar */}
        <div className="nav-sidebar" style={styles.navSidebar}>
          <div className="sticky-selectors">

            {/* ── Week Indicator (read-only — no backward navigation) ── */}
            {weeks.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', paddingLeft: '2px' }}>
                  <span style={{ ...typography.label, color: colors.textMuted }}>CURRENT WEEK</span>
                  <span
                    title="Weeks progress automatically as you complete them. Completed weeks are read-only."
                    style={{ ...typography.tiny, color: colors.textMuted, cursor: 'help', opacity: 0.6 }}
                  >
                    ⓘ
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  backgroundColor: colors.surface2,
                  border: `1px solid ${colors.accent}`,
                  borderRadius: '8px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '22px', height: '22px', borderRadius: '50%',
                      backgroundColor: colors.accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: '700',
                      color: colors.bg,
                      flexShrink: 0,
                    }}>
                      {currentWeek?.week_number}
                    </div>
                    <span style={{ ...typography.small, color: colors.text, fontWeight: '700' }}>
                      Week {currentWeek?.week_number}
                    </span>
                  </div>
                  <span style={{ ...typography.tiny, color: colors.textMuted }}>
                    of {weeks.length}
                  </span>
                </div>
                {weekReminder && (
                  <div role="status" style={{
                    marginTop: '8px', padding: '8px 12px', borderRadius: '8px',
                    backgroundColor: 'rgba(212,175,55,0.10)', border: `1px solid rgba(212,175,55,0.35)`,
                    ...typography.small, color: colors.text,
                  }}>
                    {weekReminder.workoutsLeft === 1 ? '1 workout' : `${weekReminder.workoutsLeft} workouts`} left this week.
                    {' '}Your week ends {whenWeekEnds(weekReminder.endsAt)}.
                  </div>
                )}
              </div>
            )}

            {/* ── Day Selector ── */}
            {currentWeek?.days?.length > 0 && (
              <>
                <div style={{ ...typography.label, color: colors.textMuted, marginBottom: '10px', paddingLeft: '2px' }}>
                  WEEK {currentWeek.week_number} WORKOUTS
                </div>
                <div className="day-selector-desktop hide-scrollbar" style={styles.daySelector}>
                  {currentWeek.days.map((day, index) => {
                    const isActive = selectedDayIndex === index;
                    const completed = isDayCompleted(day);
                    const isMissed = isDayMissed(day);

                    // The day's own name ("Lower body"), on up to two lines; "Day N" when it has none.
                    // It used to be the first four letters of the first word ("LOWE").
                    const shortLabel = dayTitle(day) || `Day ${day.day_number}`;

                    const borderColor = isActive ? colors.accent
                      : completed ? colors.success
                      : isMissed ? 'rgba(142,142,147,0.3)'
                      : colors.surface3;

                    const bgColor = isActive
                      ? `rgba(212,175,55,0.12)`
                      : completed
                      ? `rgba(52,199,89,0.08)`
                      : colors.surface1;

                    return (
                      <button
                        key={day.id}
                        onClick={() => handleDayChange(index)}
                        className="day-card slide-in"
                        style={{
                          display: 'flex', flexDirection: 'row',
                          alignItems: 'center', justifyContent: 'flex-start',
                          padding: '8px 10px',
                          backgroundColor: bgColor,
                          border: `2px solid ${borderColor}`,
                          borderRadius: '14px',
                          cursor: 'pointer',
                          minWidth: 0,
                          minHeight: '52px',
                          transition: 'all 0.2s ease',
                          animationDelay: `${index * 50}ms`,
                          gap: '10px',
                          boxShadow: isActive ? `0 0 12px rgba(212,175,55,0.2)` : 'none',
                        }}
                      >
                        {isMissed ? (
                          <>
                            <span style={{ fontSize: '16px', color: colors.textMuted, lineHeight: 1, flexShrink: 0 }}>✕</span>
                            <span style={{ fontSize: '12px', color: colors.textMuted, fontWeight: '700', ...dayLabelText }}>{shortLabel}</span>
                          </>
                        ) : completed ? (
                          <>
                            <span style={{ fontSize: '16px', color: colors.success, fontWeight: '700', lineHeight: 1, flexShrink: 0 }}>✓</span>
                            <span style={{ fontSize: '12px', color: colors.success, fontWeight: '700', ...dayLabelText }}>{shortLabel}</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: isActive ? colors.accent : colors.textMuted, lineHeight: 1, flexShrink: 0 }}>
                              {day.day_number}
                            </span>
                            <span style={{ fontSize: '12px', color: isActive ? colors.accent : colors.text, fontWeight: '600', ...dayLabelText }}>
                              {shortLabel}
                            </span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {/* Body scan every four weeks (Body stats, plan 008): one slim line below the week's workouts,
              shown only when due (owner, 18 October 2026) */}
          <ScanReminder clientId={userId} />
        </div>

        {/* Main Content */}
        <div id="cipher-today-content" style={styles.mainContent}>
          {/* Today hero — greeting + single dominant next action (design audit, item 8). Below the week and its
              days, just above the session it starts (owner, 20 October 2026: program, week, days, session) */}
          {currentDay && (
            <div style={{
              position: 'relative',
              margin: '4px 0 16px',
              padding: '14px',
              background: `linear-gradient(160deg, ${colors.surface1}, ${colors.surface2})`,
              border: `1px solid ${colors.border}`,
              borderRadius: '20px',
              overflow: 'hidden',
              WebkitTextSizeAdjust: '100%',
              textSizeAdjust: '100%',
            }}>
              {/* Corner marks + session readout — restrained technical motif (item 13), not a HUD */}
              <span style={{ position: 'absolute', top: '14px', right: '14px', width: '14px', height: '14px', borderTop: `1px solid ${colors.borderAccent}`, borderRight: `1px solid ${colors.borderAccent}` }} />
              <span style={{ position: 'absolute', bottom: '14px', right: '14px', width: '14px', height: '14px', borderBottom: `1px solid ${colors.borderAccent}`, borderRight: `1px solid ${colors.borderAccent}` }} />
              <div style={{
                position: 'absolute', top: '14px', right: '34px',
                fontFamily: metricType.mono.fontFamily, fontSize: '11px', letterSpacing: '0.08em',
                color: colors.textMuted, opacity: 0.7,
              }}>
                WEEK {String(currentWeek?.week_number ?? '').padStart(2, '0')} · DAY {String(currentDay.day_number ?? '').padStart(2, '0')}
              </div>

              <div style={{ ...typography.tiny, color: colors.accent, marginBottom: '10px', fontSize: '11px', paddingRight: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getGreeting()}{firstName ? `, ${firstName.toUpperCase()}` : ''}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <h2 style={{ ...typography.h1, color: colors.text, margin: 0, fontSize: '17px', lineHeight: 1.25, letterSpacing: '0.1px' }}>
                  {dayTitle(currentDay) || `Day ${currentDay.day_number}`}
                </h2>
                {primaryMuscle && (
                  <span style={{
                    ...typography.tiny,
                    fontSize: '11px',
                    color: colors.accent,
                    backgroundColor: colors.accentSubtle,
                    padding: '3px 8px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.borderAccent}`,
                  }}>
                    {primaryMuscle}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                {duration > 0 && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                      <span style={{ ...metricType.metric, fontSize: '18px', color: colors.text }}>{duration}</span>
                      <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px' }}>MIN</span>
                    </div>
                  </div>
                )}
                {currentDay.exercises?.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                      <span style={{ ...metricType.metric, fontSize: '18px', color: colors.text }}>{currentDay.exercises.length}</span>
                      <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px' }}>EXERCISES</span>
                    </div>
                  </div>
                )}
              </div>

              {weekCompleted && weeks[selectedWeekIndex + 1] ? (
                // The week looks finished: the main action is to move on (finishWeekAndMoveOn), not to
                // reopen a day that is already logged
                <CipherButton style={{ padding: '12px', fontSize: '15px' }} onClick={finishWeekAndMoveOn} disabled={movingOn}>
                  {movingOn ? 'Moving on…' : `Go to week ${weeks[selectedWeekIndex + 1].week_number}`}
                  <ArrowRight size={18} />
                </CipherButton>
              ) : (
                <CipherButton
                  style={{ padding: '12px', fontSize: '15px' }}
                  onClick={() => document.getElementById('cipher-today-session')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  {dayCompletion.completed > 0 ? 'Continue session' : 'Start session'}
                  <ArrowRight size={18} />
                </CipherButton>
              )}

              {weeklySessionsTotal > 0 && (
                <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ ...typography.small, color: colors.textMuted }}>Weekly progress</span>
                    <span style={{ ...typography.small, color: colors.text, fontWeight: '700' }}>
                      {weeklySessionsDone} / {weeklySessionsTotal} sessions
                    </span>
                  </div>
                  <div style={{ height: '6px', borderRadius: '999px', backgroundColor: colors.surface3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${(weeklySessionsDone / weeklySessionsTotal) * 100}%`,
                      backgroundColor: colors.accent,
                      borderRadius: '999px',
                      transition: 'width 400ms ease',
                    }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {currentDay ? (
            <div key={contentKey} id="cipher-today-session" className="content-animate workout-day-card" style={styles.workoutCard}>
              {/* Day name/exercise-count/duration used to repeat here too — now
                  shown once, in the Today hero above. Only the live per-session
                  completion % (genuinely different, updates as you log) stays. */}

              {/* Workout Completion Progress Bar */}
              {dayCompletion.total > 0 && (
                <div style={styles.completionBar}>
                  <div style={styles.completionBarHeader}>
                    <span style={{ ...typography.small, color: colors.textSecondary, minWidth: 0, display: 'flex', gap: '6px' }}>
                      {dayTitle(currentDay) ? <strong style={{ color: colors.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dayTitle(currentDay)}</strong> : null}
                      <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{dayTitle(currentDay) ? '· ' : ''}{dayCompletion.completed} of {dayCompletion.total} done</span>
                    </span>
                    <span style={{ ...typography.small, color: colors.accent, fontWeight: '700', flexShrink: 0, marginLeft: '8px' }}>
                      {dayCompletion.percentage}%
                    </span>
                  </div>
                  <div style={styles.completionBarTrack}>
                    <div style={{
                      ...styles.completionBarFill,
                      width: `${dayCompletion.percentage}%`,
                      backgroundColor: dayCompletion.percentage === 100 ? colors.success : colors.accent,
                    }} />
                  </div>
                </div>
              )}


              {/* Exercise List with Skeleton Loading */}
              {exercisesLoading ? (
                <div style={styles.exerciseList}>
                  {[1, 2, 3].map(i => <ExerciseSkeleton key={i} />)}
                </div>
              ) : !(currentDay?.exercises?.length) ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 20px',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌙</div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: shared.text,
                    marginBottom: '8px',
                  }}>
                    Rest Day
                  </div>
                  <div style={{
                    fontSize: '15px',
                    color: shared.textMuted,
                    lineHeight: '1.6',
                    maxWidth: '300px',
                  }}>
                    Recovery is part of the program. Rest, hydrate, and come back stronger tomorrow.
                  </div>
                </div>
              ) : processedExercises.length > 0 ? (
                <ErrorBoundary>
                <div style={styles.exerciseList}>

                  {showSessionStart && (
                    <SessionStart
                      exerciseNames={processedExercises.filter(pe => pe.exercise?.name).map(pe => pe.exercise.name)}
                      exerciseSets={processedExercises.filter(pe => pe.exercise?.name).map(pe => Number(pe.sets) || 0)}
                      minutes={duration}
                      onBegin={beginSession}
                      onSkip={startWithoutRating}
                    />
                  )}

                  <div style={{ display: showSessionStart ? 'none' : 'contents' }}>
                  {processedExercises.map((pe, idx) => {
                    // Check if this is the first exercise in a block
                    const isFirstInBlock = pe.position_label && (idx === 0 || processedExercises[idx - 1]?.block_index !== pe.block_index);
                    const isLastInBlock = pe.is_last_in_block;
                    // Only treat as multi-exercise block if it's NOT a normal block
                    const isInMultiExerciseBlock = pe.position_label && !isLastInBlock && (idx < processedExercises.length - 1) && (processedExercises[idx + 1]?.block_index === pe.block_index) && pe.set_category !== 'normal';
                    const setCategory = pe.set_category && SET_CATEGORIES[pe.set_category];
                    const showBlockDescription = isFirstInBlock && !isLastInBlock && setCategory && pe.set_category !== 'normal';

                    return (
                    <div key={pe.id} className="fade-in" style={{ animationDelay: `${idx * 50}ms` }}>
                      {/* Block Strategy Description Banner */}
                      {showBlockDescription && (
                        <div style={{
                          background: colors.surface2,
                          padding: '14px 18px',
                          borderRadius: '14px',
                          marginBottom: '12px',
                          border: `1px solid ${colors.borderAccent}`,
                          boxShadow: `0 2px 8px ${colors.accentGlow}`,
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                          }}>
                            <span style={{
                              fontSize: '14px',
                              fontWeight: '700',
                              color: colors.accent,
                              letterSpacing: '0.5px',
                            }}>
                              {setCategory.label}
                            </span>
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: colors.textMuted,
                            lineHeight: '1.6',
                            fontStyle: 'italic',
                          }}>
                            {getClientDescription(pe.set_category)}
                          </div>
                        </div>
                      )}

                      {/* Block position label for block-based programs */}
                      {pe.position_label && (
                        <div style={{
                          ...styles.blockLabel,
                          borderLeft: isInMultiExerciseBlock || !isLastInBlock ? `4px solid ${colors.accent}` : 'none',
                          paddingLeft: isInMultiExerciseBlock || !isLastInBlock ? '16px' : '0',
                          marginLeft: isInMultiExerciseBlock || !isLastInBlock ? '8px' : '0',
                        }}>
                          {pe.set_category && pe.set_category !== 'normal' && (
                            <>
                              <span style={styles.categoryTag}>
                                {formatCategoryDisplay(pe.set_category, pe.category_params)}
                              </span>
                              <TechniqueInfoTooltip categoryId={pe.set_category} />
                            </>
                          )}
                          {isLastInBlock && pe.rest_seconds && (
                            <span style={styles.restIndicator}>
                              Rest: {pe.rest_seconds}s after all exercises
                            </span>
                          )}
                        </div>
                      )}
                      {/* Per-exercise category (for non-block programs) */}
                      {!pe.position_label && pe.set_category && pe.set_category !== 'normal' && (
                        <div style={styles.blockLabel}>
                          <span style={styles.categoryTag}>
                            {formatCategoryDisplay(pe.set_category, pe.category_params)}
                          </span>
                          <TechniqueInfoTooltip categoryId={pe.set_category} />
                        </div>
                      )}
                      <div className="workout-block-rail" style={{
                        marginLeft: isInMultiExerciseBlock || !isLastInBlock ? '8px' : '0',
                        paddingLeft: isInMultiExerciseBlock || !isLastInBlock ? '16px' : '0',
                        borderLeft: isInMultiExerciseBlock || !isLastInBlock ? `4px solid ${colors.accent}` : 'none',
                      }}>
                        <WorkoutLogger
                          programExercise={pe}
                          dayId={currentDay.id}
                          programId={program.id}
                          userId={userId}
                          assignedAt={assignedAt}
                          exerciseStatus={exerciseStatuses[pe.id]}
                          onStatusChange={(status, reason) => handleExerciseStatusChange(pe.id, status, reason)}
                          onSetCompleted={handleSetCompleted}
                          onSessionCreated={(sessionId) => handleSessionCreated(sessionId, currentDay.id)}
                          skippedReasons={SKIPPED_REASONS}
                          isLocked={sessionLocked}
                          preloadedOverride={preloadedOverrides.get(pe.id)}
                          preloadedFlag={preloadedFlags.get(pe.id)}
                          coachResolvedNote={preloadedResolvedNotes.get(pe.id) || null}
                        />
                      </div>

                      {/* Arrow Between Exercises in Block */}
                      {isInMultiExerciseBlock && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '12px 0',
                          marginLeft: '24px',
                        }}>
                          <div style={{
                            width: '0',
                            height: '0',
                            borderLeft: '10px solid transparent',
                            borderRight: '10px solid transparent',
                            borderTop: `14px solid ${colors.accent}`,
                          }} />
                        </div>
                      )}
                    </div>
                    );
                  })}
                  </div>
                </div>
                </ErrorBoundary>
              ) : (
                <div style={styles.emptyExercises}>
                  <p style={{ ...typography.body, color: colors.textMuted }}>
                    {exerciseFilter !== 'all' ? 'No exercises match this filter.' : 'No exercises added to this day yet.'}
                  </p>
                  {exerciseFilter !== 'all' && (
                    <button onClick={() => setExerciseFilter('all')} style={styles.linkButton}>Show all exercises</button>
                  )}
                </div>
              )}

              {/* Complete Day Button (Sticky) */}
              {processedExercises.length > 0 && !sessionLocked && !showSessionStart && (
                <div key={`complete-day-${contentKey}`} style={styles.completeDayContainer}>
                  <button
                    onClick={() => setFinishOpen(true)}
                    disabled={dayCompleting || !canComplete}
                    style={{
                      ...styles.completeDayBtn,
                      opacity: canComplete ? 1 : 0.5,
                      cursor: canComplete ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {dayCompleting ? (
                      <>
                        <span style={styles.btnSpinner} />
                        Completing...
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '20px' }}>✓</span>
                        Complete Day
                      </>
                    )}
                  </button>
                  {!canComplete && (
                    <p style={{ ...typography.tiny, color: colors.textMuted, textAlign: 'center', marginTop: '8px' }}>
                      Complete at least one exercise to finish the day
                    </p>
                  )}
                  <button
                    onClick={handleMarkDayMissed}
                    disabled={dayCompleting}
                    style={{
                      marginTop: '8px',
                      width: '100%',
                      padding: '10px 20px',
                      background: 'transparent',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '14px',
                      color: colors.textMuted,
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: dayCompleting ? 'not-allowed' : 'pointer',
                      opacity: dayCompleting ? 0.5 : 1,
                    }}
                  >
                    Mark as Missed
                  </button>
                </div>
              )}


              {/* Locked Session Notice — only shown when there are still days remaining in the week */}
              {sessionLocked && currentSessionId && !weekCompleted && (
                isDayMissed(currentDay)
                  ? (
                    <div style={{ ...styles.lockedNotice, flexDirection: 'column', gap: '12px', alignItems: 'center', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px', color: colors.textMuted }}>✕</span>
                        <p style={{ ...typography.small, color: colors.textMuted, margin: 0 }}>
                          Day missed
                        </p>
                      </div>
                      {nextSession && (
                        <button
                          onClick={() => navigateTo(nextSession.weekIndex, nextSession.dayIndex)}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: colors.accent,
                            border: 'none',
                            borderRadius: '14px',
                            color: colors.bg,
                            fontSize: '15px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          Next: {dayTitle(nextSession.day) || `Day ${nextSession.day.day_number}`} →
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ ...styles.lockedNotice, flexDirection: 'column', gap: '12px', alignItems: 'center', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Lock size={16} color={colors.success} />
                        <p style={{ ...typography.small, color: colors.textMuted, margin: 0 }}>
                          Workout complete — great work!
                        </p>
                      </div>
                      {nextSession && (
                        <button
                          onClick={() => navigateTo(nextSession.weekIndex, nextSession.dayIndex)}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: colors.accent,
                            border: 'none',
                            borderRadius: '14px',
                            color: colors.bg,
                            fontSize: '15px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          Next: {dayTitle(nextSession.day) || `Day ${nextSession.day.day_number}`} →
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSessionLocked(false);
                          setCurrentSessionId(null);
                          setLocalSetCompletions(new Set());
                          setExerciseStatuses({});
                        }}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: 'transparent',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '14px',
                          color: colors.textMuted,
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                        }}
                      >
                        Train Again
                      </button>
                    </div>
                  )
              )}

              {/* Week Complete Banner — shown once when all days are done */}
              {weekCompleted && (() => {
                return (
                  <div style={{
                    marginTop: '24px',
                    padding: '20px',
                    backgroundColor: colors.surface2,
                    borderRadius: '14px',
                    border: `1px solid ${colors.success + '33'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'center',
                  }}>
                    <p style={{ ...typography.small, color: colors.success, margin: 0, fontWeight: '700' }}>
                      Week complete — outstanding work!
                    </p>
                    <p style={{ ...typography.tiny, color: colors.textMuted, margin: 0 }}>
                      {generatingWeek
                        ? 'Preparing your next week...'
                        : isOnLastAvailableWeek
                          ? weekGenerationFailed
                            ? 'Having trouble loading your next week — tap below to try again.'
                            : 'Your next week is ready.'
                          : `Browse any day above to review it, or go on to week ${weeks[selectedWeekIndex + 1]?.week_number ?? ''}.`}
                    </p>
                    {!isOnLastAvailableWeek && weeks[selectedWeekIndex + 1] && (
                      <button
                        type="button"
                        onClick={finishWeekAndMoveOn}
                        disabled={movingOn}
                        style={{
                          marginTop: '4px', padding: '12px 24px', backgroundColor: colors.accent, border: 'none',
                          borderRadius: '8px', color: colors.bg, fontSize: '15px', fontWeight: '700',
                          cursor: movingOn ? 'default' : 'pointer', opacity: movingOn ? 0.6 : 1, minHeight: 44,
                        }}
                      >
                        {movingOn ? 'Moving on…' : `Go to week ${weeks[selectedWeekIndex + 1].week_number}`}
                      </button>
                    )}
                    {isOnLastAvailableWeek && !generatingWeek && (
                      <button
                        onClick={async () => {
                          setGeneratingWeek(true);
                          setWeekGenerationFailed(false);
                          try {
                            await generateNextWeek(program?.id, weeks.length);
                            await fetchUserAndProgram();
                          } catch (err) {
                            console.error('Week generation failed:', err);
                            setWeekGenerationFailed(true);
                          } finally {
                            setGeneratingWeek(false);
                          }
                        }}
                        style={{
                          marginTop: '4px',
                          padding: '10px 24px',
                          backgroundColor: colors.accent,
                          border: 'none',
                          borderRadius: '8px',
                          color: colors.bg,
                          fontSize: '14px',
                          fontWeight: '700',
                          cursor: 'pointer',
                        }}
                      >
                        {weekGenerationFailed ? 'Try again' : 'Start next week'}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : weeks.length === 0 ? (
            <div style={styles.emptyWorkout}>
              <p style={{ ...typography.body, color: colors.textMuted }}>This program doesn't have any workouts yet.</p>
            </div>
          ) : null}
        </div>
      </div>

      {finishOpen && (
        <SessionFinish
          saving={dayCompleting}
          onClose={() => setFinishOpen(false)}
          onSave={(rpe) => handleCompleteDay(rpe)}
        />
      )}

      {/* Workout Summary Modal */}
      {showSummaryModal && (
        <WorkoutSummaryModal
          sessionId={completedSessionId}
          userId={userId}
          onClose={() => {
            setShowSummaryModal(false);
            const week = weeks[selectedWeekIndex];
            const isLastDayOfWeek = selectedDayIndex === (week?.days?.length || 0) - 1;
            if (onShowProgress) {
              // To Progress, not into the next workout (owner, 23 October 2026). The next day is only
              // remembered, not opened: opening it started its session at once, so a day never trained
              // looked begun. Coming back reloads the program, so a finished week moves on as before.
              if (!isLastDayOfWeek) savePosition(selectedWeekIndex, selectedDayIndex + 1);
              onShowProgress();
            } else if (isLastDayOfWeek) {
              // Week may have auto-progressed — reload to land on new week
              fetchUserAndProgram();
            } else {
              // Advance to next day in the same week
              navigateTo(selectedWeekIndex, selectedDayIndex + 1);
            }
          }}
        />
      )}

      {/* Floating Complete Day bar — appears once any progress made, stays visible without scrolling */}
      {canComplete && !sessionLocked && !dayCompleting && !finishOpen && processedExercises?.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          background: 'linear-gradient(to top, rgba(10,14,39,0.98) 70%, rgba(10,14,39,0))',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '6px',
        }}>
          <button
            onClick={() => setFinishOpen(true)}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: colors.success,
              border: 'none',
              borderRadius: '14px',
              color: colors.bg,
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: '0 4px 20px rgba(52,199,89,0.35)',
            }}
          >
            <span style={{ fontSize: '18px' }}>✓</span>
            Complete Day
          </button>
        </div>
      )}

      {/* The welcome tour waits while the monthly check-in is open (one screen at a time) */}
      {showOnboarding && tourMayStart && !showCheckInModal && (
        <OnboardingModal onDone={() => setShowOnboarding(false)} />
      )}

      {showCheckInModal && checkInEligibility && (
        <MonthlyCheckIn
          userId={userId}
          weekNumber={checkInEligibility.nextCheckInWeek}
          onClose={() => setShowCheckInModal(false)}
          onSuccess={async () => {
            // Refresh eligibility after successful check-in (the check-in's own "sent" screen says it went)
            const eligibility = await checkEligibilityForCheckIn(userId, program.id);
            setCheckInEligibility(eligibility);
          }}
        />
      )}
    </div>
  );
};

// A day button's name: beside the number, at most two lines, cut with an ellipsis after that.
const dayLabelText = {
  lineHeight: 1.25,
  textAlign: 'left',
  minWidth: 0,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
};

const styles = {
  container: {
    backgroundColor: colors.bg,
    borderRadius: '20px',
    padding: '20px',
    minHeight: '400px',
  },
  splitLayout: { display: 'block' },
  navSidebar: { marginBottom: '24px' },
  mainContent: { flex: 1, minWidth: 0 },

  // States
  stateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 24px',
    backgroundColor: colors.bg,
    borderRadius: '20px',
    textAlign: 'center',
  },
  illustrationContainer: { marginBottom: '8px' },
  actionButtons: { display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' },
  primaryButton: {
    padding: '14px 28px',
    backgroundColor: colors.accent,
    color: colors.bg,
    border: 'none',
    borderRadius: '14px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '14px 28px',
    backgroundColor: 'transparent',
    color: colors.text,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  linkButton: {
    marginTop: '16px',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: colors.accent,
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },

  // Header
  // Relative, so the LIVE badge sits in the header's corner, not over the computer's top bar
  header: { position: 'relative', marginBottom: '24px', backgroundColor: colors.bg },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' },
  headerLeft: { flex: 1, minWidth: 0 },
  checkInBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: colors.accent,
    borderRadius: '14px',
    marginTop: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)',
    gap: '16px',
    flexWrap: 'wrap',
  },
  checkInButton: {
    padding: '10px 20px',
    backgroundColor: colors.bg,
    color: colors.accent,
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
  },

  // Week selector
  weekSelector: { display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' },
  weekTab: {
    padding: '14px 18px',
    backgroundColor: colors.surface1,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.border,
    borderRadius: '14px',
    color: colors.textSecondary,
    fontSize: '14px',
    cursor: 'pointer',
    minWidth: '130px',
    textAlign: 'left',
  },
  weekTabActive: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  weekTabContent: { display: 'flex', flexDirection: 'column', gap: '8px' },
  progressBar: { height: '3px', backgroundColor: colors.surface3, borderRadius: '4px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '4px', transition: 'width 0.4s ease' },

  // Day selector
  // Two to a row on a phone, more on a wide screen; nothing runs off the side.
  daySelector: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', paddingBottom: '8px' },
  dayCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: colors.surface1,
    border: `1px solid ${colors.border}`,
    borderRadius: '14px',
    cursor: 'pointer',
    minWidth: '130px',
    textAlign: 'left',
  },
  dayCardActive: {
    backgroundColor: colors.surface2,
    border: `1px solid ${colors.borderAccent}`,
    boxShadow: `0 0 0 1px ${colors.borderAccent}`,
  },
  dayCardComplete: {
    opacity: 0.75,
  },
  completionRing: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    borderWidth: '2px',
    borderStyle: 'solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.25s ease',
  },
  dayInfo: { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 },

  // Workout card
  workoutCard: {
    backgroundColor: colors.surface1,
    borderRadius: '20px',
    padding: '24px',
    border: `1px solid ${colors.border}`,
  },
  workoutHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  workoutTitleRow: { display: 'flex', alignItems: 'center', gap: '16px' },

  // Completion progress bar
  completionBar: {
    padding: '12px 16px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    marginBottom: '20px',
    border: `1px solid ${colors.border}`,
    // Stays at the top of the screen while the exercises scroll under it.
    position: 'sticky',
    top: '8px',
    zIndex: 5,
  },
  completionBarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  completionBarTrack: {
    height: '6px',
    backgroundColor: colors.surface3,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  completionBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease, background-color 0.3s ease',
  },

  // Summary row
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '20px 24px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    marginBottom: '20px',
    border: `1px solid ${colors.border}`,
    flexWrap: 'wrap',
    gap: '16px',
  },
  summaryItem: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '100px' },
  summaryIcon: { fontSize: '24px' },
  summaryDivider: { width: '1px', height: '40px', backgroundColor: colors.border },

  // Filter bar
  filterBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  filterGroup: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  filterBtn: {
    padding: '8px 14px',
    backgroundColor: colors.surface2,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.textMuted,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  filterBtnActive: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
    color: colors.bg,
  },

  // Exercise list
  exerciseList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  emptyExercises: { textAlign: 'center', padding: '48px 24px' },

  // Block labels for block-based programs
  blockLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  },
  positionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '28px',
    height: '22px',
    padding: '0 8px',
    backgroundColor: colors.accent,
    color: colors.bg,
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
  },
  categoryTag: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: colors.surface3,
    color: colors.textSecondary,
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '600',
  },
  restIndicator: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: colors.textMuted,
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '500',
  },

  emptyWorkout: {
    textAlign: 'center',
    padding: '48px 24px',
    backgroundColor: colors.surface1,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
  },

  // RPE Section
  rpeSection: {
    marginTop: '24px',
    padding: '20px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
  },
  rpeButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    flexWrap: 'wrap',
  },
  rpeBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface3,
    color: colors.textSecondary,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  rpeBtnActive: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
    color: colors.bg,
  },

  // Complete Day Button
  completeDayContainer: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
  },
  completeDayBtn: {
    width: '100%',
    padding: '18px 24px',
    backgroundColor: colors.success,
    border: 'none',
    borderRadius: '14px',
    color: colors.bg,
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'all 0.2s ease',
  },
  btnSpinner: {
    width: '20px',
    height: '20px',
    border: `3px solid rgba(255,255,255,0.3)`,
    borderTop: '3px solid white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  // Locked Notice
  lockedNotice: {
    marginTop: '24px',
    padding: '20px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    backgroundColor: colors.surface1,
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    border: `1px solid ${colors.border}`,
  },
  modalIcon: {
    fontSize: '48px',
  },
  modalStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  modalStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  modalSkipped: {
    padding: '16px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    marginBottom: '24px',
    textAlign: 'left',
  },
  modalCloseBtn: {
    width: '100%',
    padding: '16px',
    backgroundColor: colors.accent,
    border: 'none',
    borderRadius: '14px',
    color: colors.bg,
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
  },
};

export default ClientProgramView;
